import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RootState, AppDispatch } from "configureStore";
import {
  fetchOnboardingDetails,
  setDestroy,
} from "./onboarding-container.slice";
import OnboardingViewSkeleton from "./onboarding-view-skeleton";
import {
  setModalxComponent,
  setModalxComponentProps,
  setIsModalxOpen,
  setModalxStyle,
  setModalxOptions,
} from "components/modalx/modalx.slice";
import {
  completeStep,
  completeStep1,
  generateStep,
  sendForSignature,
  revokeMedicalApproval,
  sendLoginLink,
  getKioskCode,
  deleteOnboarding,
  updateOnboarding,
  updateOnboardingAdmin,
  uploadOnboardingFile,
  deleteOnboardingFile,
  uploadSignedDocument,
  reactivateOnboarding,
  reopenStep,
  unlockStep,
  activateUser,
  sendSignatureReminder,
  fetchActivationData,
  completeActivation,
  fetchOnboardingWorkSecurityStatus,
  completeWorkSecurityStep,
  resendWorkSecuritySignature,
} from "services/onboarding.service";
import ApprovalStatusLine from "features/work-security-approvals/ApprovalStatusLine";
import { useOnboardingApprovals } from "features/work-security-approvals/useOnboardingApprovals";
import {
  isWaitingForBlockingApprovals,
  shouldOfferManualWsComplete,
} from "features/work-security-approvals/approvals-helpers";
import { OnboardingWorkSecurityStatus } from "types/Onboarding";
import { PermissionCatalogueEntry } from "types/Permissions";
import { Badge } from "@shopify/polaris";
import DepartmentMultiSelect from "components/department-multi-select/department-multi-select";
import { fetchAssignableDepartments } from "services/departments.service";
import { onboardingDepartmentErrorKey } from "containers/departments/department-errors";
import { upload, isEmptyUploadError } from "services/upload-file.service";
import {
  getOnboardingStatusTone,
  getOnboardingStatusLabel,
  getSignatureUserLabel,
  getStepFileId,
  STEP_LABELS,
  ALL_STEPS,
  STEP_2_FILE_TYPES,
  STEP_2_QUESTIONNAIRE_FILE_TYPE,
} from "utils/onboardingStatus";
import { dedupeSignaturesByUser } from "utils/signatureUtils";
import { isFileNotAFreshUpload, isDocumentAlreadySigned, alreadyEmployedHereMessage } from "utils/onboarding-errors";
import UserSelect from "features/user-select/user-select";
import PermissionPicker from "components/permission-picker/PermissionPicker";
import axios from "axios";
import SignMethodSelect, { SignMethodValue } from "features/sign-method-select/SignMethodSelect";
import ConfirmDialog from "components/confirm-dialog/confirm-dialog";
import { useConfirmDialog } from "components/confirm-dialog/use-confirm-dialog";
import ContractAmendmentsPanel from "features/contract-amendments/ContractAmendmentsPanel";
import { computePlannedSalaryChange } from "containers/my-onboarding/salary-change-helpers";
import { API_URL } from "../../constants";
import { toast } from "sonner";
import { AuditLogPopover } from "components/audit-log/audit-log-popover";
import {
  canLeadHiring as getCanLeadHiring,
  canManageOnboarding as getCanManageOnboarding,
  isMaster as getIsMaster,
} from "utils/onboardingPermissions";
import OnboardingJobLeaveSection from "./onboarding-job-leave-section";
import OnboardingSalary from "./onboarding-salary";
import EmployeeFormPrefill, { canPrefillEmployeeForm } from "./employee-form-prefill";
import OnboardingAssetRequestLine from "features/asset-requests/OnboardingAssetRequestLine";
import { fetchJobLeavesByOnboarding } from "services/job-leave.service";
import {
  ALL_JOB_LEAVE_STEPS,
  JOB_LEAVE_STEP_LABELS,
  JobLeaveStep,
  getJobLeaveStepIndex,
  getJobLeaveStatusLabel,
  getJobLeaveStatusTone,
  normalizeJobLeaveStatus,
} from "utils/jobLeaveStatus";

function LockIcon({ className = "w-3.5 h-3.5 shrink-0" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden
    >
      <path
        fillRule="evenodd"
        d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z"
        clipRule="evenodd"
      />
    </svg>
  );
}

type DepartmentOption = { id: string; name: string };

/**
 * The departments the activation card offers and pre-selects as the
 * employee's memberships. HR (`assignsRights`) may assign any department; a
 * hiring head only the ones he heads (the backend refuses others with
 * DEPARTMENT_NOT_IN_SCOPE).
 *   - HR, list loaded   → offered: every department plus the record's tags; pre-selected: all tags.
 *   - head, list loaded → offered: his departments; pre-selected: the record's tags among them.
 *   - list failed (null) → HR: the record's tags, offered and pre-selected; head: nothing.
 */
export function activationDepartmentChoices(
  assignable: DepartmentOption[] | null,
  tags: DepartmentOption[],
  assignsRights: boolean
): { options: DepartmentOption[]; preselected: string[] } {
  if (!assignsRights) {
    if (!assignable) return { options: [], preselected: [] };
    const headedIds = new Set(assignable.map((d) => d.id));
    return {
      options: assignable,
      preselected: tags.map((d) => d.id).filter((id) => headedIds.has(id)),
    };
  }
  const optionsById = new Map<string, DepartmentOption>();
  for (const d of [...(assignable || []), ...tags]) {
    optionsById.set(d.id, { id: d.id, name: d.name });
  }
  return {
    options: Array.from(optionsById.values()),
    preselected: tags.map((d) => d.id),
  };
}

const badgeToneClasses: Record<string, string> = {
  success: "bg-green-100 text-green-800",
  critical: "bg-red-100 text-red-800",
  attention: "bg-yellow-100 text-yellow-800",
  info: "bg-blue-100 text-blue-800",
};

function OnboardingViewContainer() {
  const dispatch: AppDispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const lang = ((i18n as any).language || "en") as "lt" | "en";
  const { companyId, onboardingId } = useParams<{
    companyId: string;
    onboardingId: string;
  }>();
  const navigate = useNavigate();

  const { onboardingEntity, isError, requestedOnboardingId } = useSelector(
    (state: RootState) => state.onboardingContainerReducer
  );
  const session = useSelector((state: RootState) => state.authReducer.session);
  const role = useSelector((state: RootState) => state.authReducer.role);
  const currentUserId = session?.id;

  // HR only: job leave, amendments (with their documents and the WS
  // revision), early activation, the History log, the WS step's manual actions.
  const canManageOnboarding = getCanManageOnboarding(role);
  // HR or a hiring head on a record of his departments (the backend gives a
  // head only those): the hire itself — documents, signing, kiosk, employee
  // data, medical check, the work-security step card, activation.
  const leadsThisHire = getCanLeadHiring(role);
  // Department block + its update action are Master-only (not general managers).
  const isMaster = getIsMaster(role);

  const [signMethodValue, setSignMethodValue] = useState<SignMethodValue>({ method: "dokobit" });
  const [actionLoading, setActionLoading] = useState(false);
  const { confirmState, askConfirm, handleConfirmYes, closeConfirm } = useConfirmDialog();
  const [uploadingType, setUploadingType] = useState<string | null>(null);
  const [kioskCode, setKioskCode] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [resentIds, setResentIds] = useState<Set<string>>(new Set());

  // Step 5: Activation state
  // Rights given by hand at activation; the position's own rights come on top
  // and are shown locked. The picker offers the list fetch-activation-data sends.
  const [activationPermissions, setActivationPermissions] = useState<string[]>([]);
  const [permissionCatalogue, setPermissionCatalogue] = useState<PermissionCatalogueEntry[]>([]);
  const [positionPermissions, setPositionPermissions] = useState<string[]>([]);
  const [canAssignMaster, setCanAssignMaster] = useState(false);
  const [activationCategories, setActivationCategories] = useState<string[]>([]);
  const [activationManager, setActivationManager] = useState<string>("");
  const [categoryTree, setCategoryTree] = useState<any[]>([]);
  const [activationDataLoaded, setActivationDataLoaded] = useState(false);
  // Set from fetch-activation-data before the activation card renders (it
  // shows a spinner until activationDataLoaded); false for a hiring head.
  const [canAssignRights, setCanAssignRights] = useState(true);
  // Department memberships to assign at activation (DepartmentsUsers), plus the
  // pickable options — see activationDepartmentChoices.
  const [activationDepartments, setActivationDepartments] = useState<string[]>(
    []
  );
  const [assignableDepartments, setAssignableDepartments] = useState<
    DepartmentOption[]
  >([]);

  // Work Security step state — loaded at work_security AND step_5 (activation
  // is blocked server-side while unsigned WS documents exist).
  const [wsStatus, setWsStatus] = useState<OnboardingWorkSecurityStatus | null>(
    null
  );
  const [wsLoading, setWsLoading] = useState(false);
  const [wsLoadFailed, setWsLoadFailed] = useState(false);
  const wsRequestRef = useRef(0);
  const [wsResendingFileId, setWsResendingFileId] = useState<string | null>(null);
  const [wsResentFileIds, setWsResentFileIds] = useState<Set<string>>(new Set());

  // Admin Section B editing state
  const [editingAdmin, setEditingAdmin] = useState(false);
  const [adminFields, setAdminFields] = useState<Record<string, any>>({});

  // Company signer state
  const [companySigner, setCompanySigner] = useState("");

  // Lifecycle tabs: Onboarding | Leave (Leave locked until hire is completed)
  const [lifecycleTab, setLifecycleTab] = useState<"onboarding" | "leave">(
    "onboarding"
  );
  const [leaveProcessActive, setLeaveProcessActive] = useState(false);
  const [leaveStickyStatus, setLeaveStickyStatus] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (companyId && onboardingId) {
      dispatch(fetchOnboardingDetails({ companyId, onboardingId }));
    }
    return () => {
      dispatch(setDestroy());
    };
  }, [dispatch, companyId, onboardingId]);

  // Reset lifecycle UI when navigating to another onboarding record.
  useEffect(() => {
    setLifecycleTab("onboarding");
    setLeaveProcessActive(false);
    setLeaveStickyStatus(null);
  }, [onboardingId]);

  // Detect existing leave so we can open Leave tab + lock onboarding edits.
  useEffect(() => {
    const status = onboardingEntity?.status;
    const id = onboardingEntity?.id;
    if (!canManageOnboarding || !companyId || !id) {
      return;
    }
    if (status !== "completed") {
      setLeaveProcessActive(false);
      setLifecycleTab("onboarding");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchJobLeavesByOnboarding(companyId, id);
        if (cancelled) return;
        const relevant = rows.filter((r) => {
          const s = normalizeJobLeaveStatus(r.status);
          return s !== "cancelled";
        });
        const hasLeave = relevant.length > 0;
        setLeaveProcessActive(hasLeave);
        if (hasLeave) {
          setLifecycleTab("leave");
          const active = relevant.find(
            (r) => normalizeJobLeaveStatus(r.status) !== "completed"
          );
          const completed = relevant.find(
            (r) => normalizeJobLeaveStatus(r.status) === "completed"
          );
          const preferred = active || completed || null;
          setLeaveStickyStatus(
            preferred ? normalizeJobLeaveStatus(preferred.status) : null
          );
        } else {
          setLeaveStickyStatus(null);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    canManageOnboarding,
    companyId,
    onboardingEntity?.id,
    onboardingEntity?.status,
  ]);

  // Load the Step-5 activation form data — hire leaders only, so the request
  // never fires for other viewers. The dependency list is intentionally
  // limited to stable primitives (leadsThisHire / status / id / companyId); it
  // deliberately does NOT include `onboardingEntity.departments`.
  // A later onboarding refetch — reload() or a departments update — produces a
  // new entity reference but the SAME status/id, so this loader does not re-run
  // and cannot clobber the manager's in-progress activation selections.
  useEffect(() => {
    if (
      !(
        leadsThisHire &&
        onboardingEntity?.status === "step_5" &&
        companyId &&
        onboardingEntity.id
      )
    ) {
      return;
    }

    (async () => {
      try {
        const data = await fetchActivationData(companyId, onboardingEntity.id!);
        // A hiring head's activation gives personal-area access only — the
        // backend leaves rights and folders untouched, so no pickers for them.
        const assignsRights = data.canAssignRights;
        setCanAssignRights(assignsRights);
        if (assignsRights) {
          setPermissionCatalogue(data.catalogue);
          setActivationPermissions(data.manualPermissions);
          setPositionPermissions(data.positionPermissions);
          setCanAssignMaster(data.canAssignMaster);
        }
        setActivationCategories(data.currentCategories || []);
        setActivationManager(data.currentManager || "");
        if (assignsRights && data.userId) {
          const treeRes = await axios.get(
            `${API_URL}/${companyId}/files/fetch-tree-file-categories/${data.userId}`
          );
          setCategoryTree(treeRes.data || []);
        }
        const tags: DepartmentOption[] = (onboardingEntity.departments || []).map(
          (d: any) => ({ id: d.id, name: d.name })
        );
        let assignable: DepartmentOption[] | null = null;
        try {
          assignable = (await fetchAssignableDepartments(companyId)).departments;
        } catch {
          // Transient error: activationDepartmentChoices decides the fallback.
        }
        const choices = activationDepartmentChoices(assignable, tags, assignsRights);
        setAssignableDepartments(choices.options);
        setActivationDepartments(choices.preselected);
        setActivationDataLoaded(true);
      } catch {
        toast.error(t("error"));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadsThisHire, onboardingEntity?.status, onboardingEntity?.id, companyId]);

  // Work-security compliance status — relevant at the work_security step itself
  // (the step card) and at step_5 (unsigned-documents warning + activation gate).
  const isWsStatusRelevant =
    leadsThisHire &&
    (onboardingEntity?.status === "work_security" ||
      onboardingEntity?.status === "step_5");

  const loadWsStatus = async () => {
    if (!isWsStatusRelevant || !companyId || !onboardingEntity?.id) return;
    // Guard against out-of-order responses: only the latest request may
    // touch state (a slow response for a previous onboarding must be dropped).
    const requestId = ++wsRequestRef.current;
    setWsLoading(true);
    setWsLoadFailed(false);
    try {
      const data = await fetchOnboardingWorkSecurityStatus(
        companyId,
        onboardingEntity.id!
      );
      if (wsRequestRef.current === requestId) {
        setWsStatus(data);
      }
    } catch {
      if (wsRequestRef.current === requestId) {
        setWsLoadFailed(true);
        toast.error(t("error"));
      }
    } finally {
      if (wsRequestRef.current === requestId) setWsLoading(false);
    }
  };

  useEffect(() => {
    // Invalidate any in-flight request and never carry another record's data
    wsRequestRef.current++;
    setWsStatus(null);
    setWsLoadFailed(false);
    if (!isWsStatusRelevant) {
      return;
    }
    setWsResentFileIds(new Set());
    loadWsStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadsThisHire, onboardingEntity?.status, onboardingEntity?.id, companyId]);

  // Per-sphere pareigybės approvals of the hire (positions catalogue §1 rule
  // 7). Served inside work-security-status when the backend puts them there;
  // otherwise fetched from their own endpoint once the status has loaded.
  const wsStatusHasApprovals = Array.isArray(wsStatus?.approvals);
  const onboardingApprovals = useOnboardingApprovals(
    companyId,
    onboardingEntity?.id,
    onboardingEntity?.status === "work_security" && !!wsStatus && !wsStatusHasApprovals,
    wsStatus?.approvals
  );
  const hireApprovals = (
    wsStatusHasApprovals ? wsStatus!.approvals! : onboardingApprovals.rows
  ).filter((row) => !row.amendmentId && row.kind !== "amendment");
  const refreshWsStep = () => {
    loadWsStatus();
    if (!wsStatusHasApprovals) onboardingApprovals.reload();
  };

  useEffect(() => {
    if (onboardingEntity) {
      setKioskCode(onboardingEntity.kioskCode || null);
      setCompanySigner(
        typeof onboardingEntity.companySigner === "object"
          ? (onboardingEntity.companySigner as any)?.id || ""
          : onboardingEntity.companySigner || ""
      );
      setAdminFields({
        workEmail: onboardingEntity.workEmail || "",
        needsNewPhone: onboardingEntity.needsNewPhone || "",
        phoneDevice: onboardingEntity.phoneDevice || "",
        needsComputer: onboardingEntity.needsComputer || "",
        computerDetails: onboardingEntity.computerDetails || "",
        needsServerAccess: !!onboardingEntity.needsServerAccess,
        serverName: onboardingEntity.serverName || "",
        folderAccess: onboardingEntity.folderAccess || "",
        companyCarType: onboardingEntity.companyCarType || "",
        needsFuelCard: !!onboardingEntity.needsFuelCard,
        hasFuelLimit: !!onboardingEntity.hasFuelLimit,
        fuelLimit: onboardingEntity.fuelLimit != null ? String(onboardingEntity.fuelLimit) : "",
        adminNotes: onboardingEntity.adminNotes || "",
      });
    }
  }, [onboardingEntity]);

  // The loaded onboarding matches the route only when its id equals
  // onboardingId — guards against briefly showing the previous onboarding's
  // data after navigating to a different one (the route reconciles instead of
  // remounting).
  const isOnboardingReady =
    !!onboardingEntity && onboardingEntity.id === onboardingId;

  // If loading the requested onboarding failed, show an error with a retry path
  // instead of an endless skeleton. requestedOnboardingId scopes the error to
  // the onboarding that actually failed (the slice clears it when a new fetch
  // starts), so a stale error never blocks a different onboarding, and a failed
  // background refetch (when valid data is already shown) keeps the page visible.
  if (isError && requestedOnboardingId === onboardingId && !isOnboardingReady) {
    return (
      <div className="px-6 py-4">
        <button
          onClick={() => navigate(`/app/${companyId}/onboarding`)}
          className="text-sm text-blue-600 hover:underline mb-3 flex items-center gap-1"
        >
          &larr; {t("hr_module")}
        </button>
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg flex items-center justify-between gap-4 flex-wrap">
          <span>{t("not_fetch", { defaultValue: "Could not load onboarding" })}</span>
          <button
            onClick={() => {
              if (companyId && onboardingId) {
                dispatch(fetchOnboardingDetails({ companyId, onboardingId }));
              }
            }}
            className="px-3 py-1.5 bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-100 text-sm font-medium max-md:min-h-11"
          >
            {t("retry", { defaultValue: "Retry" })}
          </button>
        </div>
      </div>
    );
  }

  if (!isOnboardingReady) {
    return <OnboardingViewSkeleton />;
  }

  const ob = onboardingEntity;
  const onboardingRecordId = ob.id;
  const currentStepIndex = ALL_STEPS.indexOf(ob.status as any);
  const createdByName = typeof ob.createdBy === "object" ? ob.createdBy?.name : ob.createdBy;
  const createdById = typeof ob.createdBy === "object" ? ob.createdBy?.id : ob.createdBy;
  const managerName = typeof ob.manager === "object" ? (ob.manager as any)?.name : ob.manager;
  const isTerminal = ob.status === "completed" || ob.status === "cancelled";
  const leaveTabLocked = ob.status !== "completed";
  const leaveLocked = leaveProcessActive && ob.status === "completed";
  const showLifecycleTabs = canManageOnboarding && ob.status !== "cancelled";
  const membershipDisabled = !!(ob as { membershipDisabled?: boolean })
    .membershipDisabled;

  const headerStatusLabel =
    leaveProcessActive && leaveStickyStatus
      ? leaveStickyStatus === "completed"
        ? t("left_company")
        : `${t("employment_leave")}: ${getJobLeaveStatusLabel(
            leaveStickyStatus,
            t,
            lang
          )}`
      : getOnboardingStatusLabel(ob.status, t, lang);
  const headerStatusTone =
    leaveProcessActive && leaveStickyStatus
      ? leaveStickyStatus === "completed"
        ? "critical"
        : getJobLeaveStatusTone(leaveStickyStatus)
      : getOnboardingStatusTone(ob.status);
  const headerBadgeClasses =
    badgeToneClasses[headerStatusTone as string] || "bg-gray-100 text-gray-800";

  const lifecycleTabsBar = showLifecycleTabs ? (
    <div className="border-b border-gray-200">
      <div className="flex items-stretch gap-1" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={lifecycleTab === "onboarding"}
          onClick={() => setLifecycleTab("onboarding")}
          className={`px-4 py-2.5 max-md:min-h-11 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-1.5 transition-colors ${
            lifecycleTab === "onboarding"
              ? "border-blue-600 text-blue-700"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
          title={
            leaveLocked
              ? t("onboarding_locked_during_leave", {
                  defaultValue:
                    "Onboarding is locked while the employment leave process is open.",
                })
              : undefined
          }
        >
          {leaveLocked && <LockIcon />}
          {t("tab_onboarding", { defaultValue: "Onboarding" })}
        </button>

        <div className="relative group">
          <button
            type="button"
            role="tab"
            aria-selected={lifecycleTab === "leave"}
            disabled={leaveTabLocked}
            onClick={() => {
              if (!leaveTabLocked) setLifecycleTab("leave");
            }}
            className={`px-4 py-2.5 max-md:min-h-11 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-1.5 transition-colors ${
              leaveTabLocked
                ? "border-transparent text-gray-400 cursor-not-allowed"
                : lifecycleTab === "leave"
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            {leaveTabLocked && <LockIcon />}
            {t("employment_leave")}
          </button>
          {leaveTabLocked && (
            <div
              role="tooltip"
              className="pointer-events-none absolute left-1/2 top-full z-30 mt-2 w-64 -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-xs leading-relaxed text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
            >
              {t("leave_locked_until_onboarding_complete")}
              <span className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-gray-900" />
            </div>
          )}
        </div>
      </div>
      {leaveTabLocked && (
        <p className="py-1 text-xs text-gray-500 md:hidden">
          {t("leave_locked_until_onboarding_complete")}
        </p>
      )}
    </div>
  ) : null;

  const jobLeaveStepLabel = (step: JobLeaveStep) => JOB_LEAVE_STEP_LABELS[step]?.[lang] || JOB_LEAVE_STEP_LABELS[step].en;
  const onboardingStepLabel = (step: string) => STEP_LABELS[step]?.[lang] || STEP_LABELS[step]?.en || step;

  // On a phone the step strip is one line ("3/6 · Medical Check") instead of
  // up to four rows of chips; the chips stay from md up.
  const jobLeaveStepLine = () => {
    if (!leaveStickyStatus) return "";
    const stepIndex = getJobLeaveStepIndex(normalizeJobLeaveStatus(leaveStickyStatus));
    if (stepIndex < 0) return headerStatusLabel;
    return `${stepIndex + 1}/${ALL_JOB_LEAVE_STEPS.length} · ${jobLeaveStepLabel(ALL_JOB_LEAVE_STEPS[stepIndex])}`;
  };
  const onboardingStepLine = () => {
    if (currentStepIndex < 0) return headerStatusLabel;
    return `${currentStepIndex + 1}/${ALL_STEPS.length} · ${onboardingStepLabel(ob.status)}`;
  };

  const stickyStepProgress =
    lifecycleTab === "leave" && leaveStickyStatus ? (
      <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm mt-3">
        <p className="text-sm font-medium md:hidden">{jobLeaveStepLine()}</p>
        <div className="hidden items-center gap-2 flex-wrap md:flex">
          {ALL_JOB_LEAVE_STEPS.map((step, idx) => {
            const stepLabel = jobLeaveStepLabel(step);
            const status = normalizeJobLeaveStatus(leaveStickyStatus);
            const stepIndex = getJobLeaveStepIndex(status);
            const isCurrent = status === step;
            const isDone =
              status === "completed" || (stepIndex >= 0 && idx < stepIndex);
            return (
              <div
                key={step}
                className={`px-3 py-1.5 rounded-lg min-w-[80px] text-center ${
                  isCurrent
                    ? "bg-blue-500 text-white"
                    : isDone
                      ? "bg-green-500 text-white"
                      : "bg-gray-100 text-gray-500"
                }`}
              >
                <p className={`text-sm ${isCurrent ? "font-bold" : ""}`}>
                  {`${idx + 1}. ${stepLabel}`}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    ) : lifecycleTab === "onboarding" || !showLifecycleTabs ? (
      <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm mt-3">
        <p className="text-sm font-medium md:hidden">{onboardingStepLine()}</p>
        <div className="hidden items-center gap-2 flex-wrap md:flex">
          {ALL_STEPS.map((step, idx) => {
            const stepLabel = onboardingStepLabel(step);
            const isCurrent = ob.status === step;
            const isDone =
              currentStepIndex > idx || ob.status === "completed";
            return (
              <div
                key={step}
                className={`px-3 py-1.5 rounded-lg min-w-[80px] text-center ${
                  isCurrent
                    ? "bg-blue-500 text-white"
                    : isDone
                      ? "bg-green-500 text-white"
                      : "bg-gray-100"
                }`}
              >
                <p className={`text-sm ${isCurrent ? "font-bold" : ""}`}>
                  {`${idx + 1}. ${stepLabel}`}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    ) : null;

  // Check if employee has submitted required data for current step
  const isEmployeeDataReady = ob.status === "step_2"
    ? !!ob.employeeSubmitted
    : true;

  // Current step PDF and signing status
  const isStep1 = ob.status === "step_1";
  const isStep2 = ob.status === "step_2";
  const isStep3 = ob.status === "step_3";
  const isWorkSecurity = ob.status === "work_security";
  const isStep5 = ob.status === "step_5";

  // For step_2: multiple PDFs (ordered by STEP_2_FILE_TYPES so the e-signature
  // agreement is always shown first); for step_1/step_4: single PDF.
  const currentStepFiles = isStep2
    ? (ob.files || [])
      .filter((f: any) => STEP_2_FILE_TYPES.includes(f.fileType))
      .slice()
      .sort(
        (a: any, b: any) =>
          STEP_2_FILE_TYPES.indexOf(a.fileType) - STEP_2_FILE_TYPES.indexOf(b.fileType)
      )
    : (ob.files || []).filter((f: any) => f.fileType === `${ob.status}_pdf`);
  const currentUserPendingStepFile = currentStepFiles.find((stepFile: any) =>
    (stepFile.signatures || []).some((sig: any) => {
      const sigUserId = typeof sig.user === "object" ? sig.user?.id : sig.user;
      return sigUserId === currentUserId && sig.status === "pending";
    })
  );
  const currentUserPendingStepFileId = getStepFileId(currentUserPendingStepFile);
  const currentStepSignatures: any[] = currentStepFiles.flatMap((f: any) => f.signatures || []);
  const currentStepSignaturesByUser: any[] = dedupeSignaturesByUser(
    currentStepSignatures,
    {
      includeResendSignatureId: true,
      includeLatestSigningTime: true,
    }
  );
  const hasCurrentStepDoc = currentStepFiles.length > 0;
  // Cancelling a document on the tablet deletes its signatures. The others may all be
  // signed, which would read as "all signed" while the step cannot advance.
  const tabletDocumentCancelled = currentStepSignatures.length > 0 &&
    currentStepFiles.some(
      (sf: any) =>
        sf.file?.signType === "Signontab" && !sf.file?.isSigned && (sf.signatures || []).length === 0
    );
  const allSigned = hasCurrentStepDoc && currentStepSignatures.length > 0 &&
    currentStepSignatures.every((s: any) => s.status === "signed") && !tabletDocumentCancelled;
  const hasPendingSignatures = hasCurrentStepDoc && currentStepSignatures.length > 0 &&
    currentStepSignatures.some((s: any) => s.status === "pending");
  // A document sent to a SignOnTab tablet is signed there: the Dokobit sign link
  // and the e-mail reminders would lead to a page where nothing can be signed.
  const stepSignsOnTablet = currentStepFiles.some(
    (sf: any) =>
      sf.file?.signType === "Signontab" &&
      (sf.signatures || []).some((sig: any) => sig.status === "pending")
  );
  // Documents sent "for paper" are marked Manual on the server, and so is each scan once uploaded.
  // Remembering it keeps the upload buttons after a reload, when the method selector is back to
  // its default. The questionnaire is informational and has no scan.
  const stepHasPaperDocuments = currentStepFiles.some(
    (sf: any) => sf.fileType !== STEP_2_QUESTIONNAIRE_FILE_TYPE && sf.file?.signType === "Manual"
  );
  const showManualUpload =
    !isStep1 && hasCurrentStepDoc && !allSigned && (signMethodValue.method === "manual" || stepHasPaperDocuments);

  // Medical files (step 3)
  const medicalFiles = (ob.files || []).filter((f: any) => f.fileType === "medical_certificate");
  const hasMedicalFiles = medicalFiles.length > 0;

  const reload = () => {
    if (companyId && ob.id) {
      dispatch(fetchOnboardingDetails({ companyId, onboardingId: ob.id }));
    }
  };

  // --- Edit handlers with document deletion warning ---

  const handleEdit = () => {
    dispatch(setModalxComponent("OnboardingForm"));
    dispatch(setModalxComponentProps({ id: ob.id }));
    dispatch(setIsModalxOpen(true));
  };

  const handleSaveAdmin = async () => {
    if (!companyId || !ob.id) return;
    setActionLoading(true);
    try {
      await updateOnboardingAdmin(companyId, ob.id, {
        ...adminFields,
        fuelLimit: adminFields.fuelLimit ? parseFloat(adminFields.fuelLimit) : null,
      });
      setEditingAdmin(false);
      reload();
      toast.success(t("saved"));
    } catch {
      toast.error(t("error"));
    } finally {
      setActionLoading(false);
    }
  };

  // --- Document generation ---

  const handleGenerateDocument = async () => {
    if (!companyId || !ob.id) return;
    setActionLoading(true);
    try {
      await generateStep(companyId, ob.id);
      reload();
      toast.success(t("document_generated"));
    } catch (e: any) {
      const data = e?.response?.data;
      // A second salary is planned but this company's contract template is
      // custom (can't print the two-period clause) — no date/amount is
      // wrong here, so the generic message would be misleading.
      if (data?.error === "SALARY_CHANGE_INVALID" && data?.reason === "custom_contract_template") {
        toast.error(t("salary_change_invalid_custom_template"));
      } else {
        toast.error(data?.error || data?.message || t("error"));
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendForSignature = async () => {
    if (!companyId || !ob.id) return;
    setActionLoading(true);
    try {
      await sendForSignature(
        companyId,
        ob.id,
        signMethodValue.method === "signontab"
          ? { signMethod: "signontab", selectedTabUser: signMethodValue.selectedTabUser }
          : { signMethod: signMethodValue.method }
      );
      reload();
      toast.success(t("sent_for_signature"));
    } catch (e: any) {
      const data = e?.response?.data;
      if (signMethodValue.method === "signontab" && e?.response?.status >= 500) {
        // Documents sent before the failure stay on the tablet; show them as waiting.
        reload();
        // The server's 5xx text for SignOnTab is English; show ours instead.
        toast.error(
          t(data?.code === "SIGNONTAB_STILL_ON_TABLET" ? "onboarding_signontab_still_on_tablet" : "onboarding_signontab_send_failed")
        );
      } else {
        toast.error(data?.error || data?.message || t("error"));
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteStep1 = () => {
    if (!companyId || !ob.id) return;
    askConfirm(t("confirm_complete_step_1"), async () => {
      setActionLoading(true);
      try {
        await completeStep1(companyId!, ob.id!);
        reload();
        toast.success(t("step_completed"));
      } catch (e: any) {
        toast.error(e?.response?.data?.error || e?.response?.data?.message || t("error"));
      } finally {
        setActionLoading(false);
      }
    });
  };

  const openApproveMedical = () => {
    if (!companyId || !ob.id) return;
    dispatch(setModalxOptions({ title: t("approve_medical_check") }));
    dispatch(setModalxComponent("ApproveMedicalModal"));
    dispatch(setModalxComponentProps({ companyId, onboardingId: ob.id }));
  };

  const handleRevokeMedical = () => {
    if (!companyId || !ob.id) return;
    askConfirm(t("confirm_revoke_medical"), async () => {
      setActionLoading(true);
      try {
        await revokeMedicalApproval(companyId!, ob.id!);
        reload();
        toast.success(t("medical_approval_revoked"));
      } catch (e: any) {
        toast.error(e?.response?.data?.error || e?.response?.data?.message || t("error"));
      } finally {
        setActionLoading(false);
      }
    });
  };

  const handleResendSignature = async (signatureId: string) => {
    if (!companyId) return;
    setResendingId(signatureId);
    try {
      await sendSignatureReminder(companyId, signatureId);
      setResentIds((prev) => new Set(prev).add(signatureId));
    } catch {
      toast.error(t("error"));
    } finally {
      setResendingId(null);
    }
  };

  // --- Sign ---

  const handleSign = (fileId: string) => {
    if (!companyId) return;
    window.open(`${API_URL}/${companyId}/dokobit/fetch-sign-url/file/${fileId}`, "_blank");
  };

  // --- Work Security step ---

  const wsTotalDocuments = (wsStatus?.roles || []).reduce(
    (sum, item) => sum + (item.documents || []).length,
    0
  );
  const wsSignedDocuments = wsTotalDocuments - (wsStatus?.unsignedCount || 0);

  // Sphere chip only earns its place once it actually distinguishes
  // something — a single-instruction-area company (today's default) must
  // keep looking exactly like it did before instruction areas existed.
  const wsShowAreaChips =
    new Set((wsStatus?.roles || []).map((item) => item.area?.id || "")).size >
    1;
  // Same idea for the Privaloma dabar / Kita split: an all-required list
  // (today's default everywhere) renders as one flat list, unchanged.
  const wsHasOptionalRoles = (wsStatus?.roles || []).some(
    (item) => item.requiredAtHiring === false
  );
  const wsRequiredRoles = (wsStatus?.roles || []).filter(
    (item) => item.requiredAtHiring !== false
  );
  const wsOptionalRoles = (wsStatus?.roles || []).filter(
    (item) => item.requiredAtHiring === false
  );

  const renderWsRoleCard = (item: NonNullable<typeof wsStatus>["roles"][number]) => (
    <div key={item.role.id} className="flex flex-col gap-2">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold">{item.role.name}</p>
          {wsShowAreaChips && item.area && (
            <Badge tone="info">{item.area.name}</Badge>
          )}
        </div>
        {item.role.description && (
          <p className="text-xs text-gray-500">{item.role.description}</p>
        )}
      </div>
      {(item.documents || []).length === 0 ? (
        <p className="text-xs text-gray-400 italic">
          {t("no_document_yet_for_role")}
        </p>
      ) : (
        item.documents.map((doc) => {
          const sigStatus = doc.signature?.status;
          return (
            <div key={doc.id} className="flex items-center gap-3 flex-wrap">
              {sigStatus === "signed" ? (
                <Badge tone="success">{t("signed")}</Badge>
              ) : sigStatus === "rejected" ? (
                <Badge tone="critical">{t("declined")}</Badge>
              ) : (
                <Badge tone="attention">{t("pending")}</Badge>
              )}
              <span className="text-sm">{doc.fileName}</span>
              {sigStatus === "rejected" && doc.signature?.declineReason && (
                <span className="text-xs text-red-600">
                  {t("reason_for_decline_signature")}: {doc.signature.declineReason}
                </span>
              )}
              <button
                className="px-2.5 py-1 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-xs font-medium max-md:min-h-11 max-md:px-3 max-md:text-sm"
                onClick={() => handlePdfView(doc.fileId)}
              >
                {t("view")}
              </button>
              <button
                className="px-2.5 py-1 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-xs font-medium max-md:min-h-11 max-md:px-3 max-md:text-sm"
                onClick={() => window.open(`${API_URL}/${companyId}/files/download/${doc.fileId}`, "_blank")}
              >
                {t("download")}
              </button>
              {sigStatus !== "signed" && canManageOnboarding && (
                wsResentFileIds.has(doc.fileId) ? (
                  <span className="text-xs text-green-600 font-medium">{t("reminder_sent")}</span>
                ) : (
                  <button
                    className="px-2 py-1 text-xs bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50 flex items-center gap-1 max-md:min-h-11 max-md:px-3 max-md:text-sm"
                    onClick={() => handleWsResend(doc.fileId)}
                    disabled={wsResendingFileId === doc.fileId}
                  >
                    {wsResendingFileId === doc.fileId && (
                      <span className="animate-spin inline-block h-3 w-3 border-2 border-gray-400 border-t-transparent rounded-full" />
                    )}
                    {t("resend")}
                  </button>
                )
              )}
            </div>
          );
        })
      )}
    </div>
  );

  const handleWsResend = async (fileId: string) => {
    if (!companyId || !ob.id) return;
    setWsResendingFileId(fileId);
    try {
      await resendWorkSecuritySignature(companyId, ob.id, fileId);
      setWsResentFileIds((prev) => new Set(prev).add(fileId));
      await loadWsStatus();
    } catch {
      toast.error(t("error"));
    } finally {
      setWsResendingFileId(null);
    }
  };

  // Nothing mandatory to sign needs no confirmation — a required role
  // without any document included: the backend skips the step by itself
  // then; this button is HR / work safety's manual path for a record still
  // waiting at the step.
  const handleCompleteWorkSecurity = async () => {
    if (!companyId || !ob.id || !wsStatus) return;
    setActionLoading(true);
    try {
      await completeWorkSecurityStep(companyId, ob.id);
      toast.success(t("step_completed"));
      reload();
    } catch (e: any) {
      const code = e?.response?.data?.error;
      if (code === "WORK_SECURITY_UNSIGNED") {
        toast.error(t("ws_error_unsigned"));
      } else if (code === "WORK_SECURITY_APPROVALS_PENDING") {
        toast.error(t("ws_error_approvals_pending"));
      } else if (code === "WORK_SECURITY_ROLES_NOT_APPLIED") {
        toast.error(t("ws_error_roles_not_applied"));
      } else if (code === "CONFLICT" || e?.response?.status === 409) {
        toast.error(t("ws_error_conflict"));
        reload();
      } else {
        toast.error(code || e?.response?.data?.message || t("error"));
      }
      await loadWsStatus();
    } finally {
      setActionLoading(false);
    }
  };

  // --- Other actions ---

  const handleSendLoginLink = async () => {
    if (!companyId || !ob.id) return;
    setActionLoading(true);
    try {
      await sendLoginLink(companyId, ob.id);
      toast.success(t("login_link_sent"));
    } catch {
      toast.error(t("error"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleGetKioskCode = async () => {
    if (!companyId || !ob.id) return;
    try {
      const res = await getKioskCode(companyId, ob.id, true);
      setKioskCode(res.kioskCode);
    } catch {
      toast.error(t("error"));
    }
  };

  const handleDelete = () => {
    if (!companyId || !ob.id) return;
    askConfirm(
      t("confirm_cancel_onboarding"),
      async () => {
        try {
          await deleteOnboarding(companyId!, ob.id!);
          navigate(`/app/${companyId}/onboarding`);
        } catch (e: any) {
          // The record moved (a step completed, an activation) between the
          // read and the cancel — show where it is now.
          if (e?.response?.status === 409) {
            toast.error(t("onboarding_cancel_conflict"));
            reload();
          } else {
            toast.error(t("error"));
          }
        }
      },
      true
    );
  };

  const handleReactivate = async () => {
    if (!companyId || !ob.id) return;
    setActionLoading(true);
    try {
      await reactivateOnboarding(companyId, ob.id);
      reload();
      toast.success(t("onboarding_reactivated"));
    } catch (e: any) {
      const alreadyEmployed = alreadyEmployedHereMessage(e);
      if (alreadyEmployed) {
        // The person works here now: no hire reopens on them.
        toast.error(t(alreadyEmployed.key, alreadyEmployed.values));
      } else if (e?.response?.data?.error === "MEMBERSHIP_DISABLED_ACTIVATED") {
        // The person once worked here and was dismissed: HR starts a new hire.
        toast.error(t("onboarding_reactivate_membership_disabled_activated"));
      } else {
        toast.error(t("error"));
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivateUser = () => {
    if (!companyId || !ob.id) return;
    askConfirm(t("confirm_activate_user"), async () => {
      setActionLoading(true);
      try {
        await activateUser(companyId!, ob.id!);
        reload();
        toast.success(t("user_activated"));
      } catch (e: any) {
        if (e?.response?.data?.error === "WORK_SECURITY_UNSIGNED") {
          toast.error(t("ws_error_unsigned"));
        } else {
          toast.error(e?.response?.data?.error || t("error"));
        }
      } finally {
        setActionLoading(false);
      }
    });
  };

  const handleReopenStep = (step: string) => {
    if (!companyId || !ob.id) return;
    askConfirm(t("confirm_reopen_step"), async () => {
      setActionLoading(true);
      try {
        await reopenStep(companyId!, ob.id!, step);
        reload();
        toast.success(t("step_reopened"));
      } catch (e: any) {
        if (e?.response?.data?.error === "signed_contract_locked") {
          toast.error(t("signed_contract_locked"));
        } else {
          toast.error(t("error"));
        }
      } finally {
        setActionLoading(false);
      }
    });
  };

  const handleUnlockStep = () => {
    if (!companyId || !ob.id) return;
    askConfirm(t("confirm_unlock_step"), async () => {
      setActionLoading(true);
      try {
        await unlockStep(companyId!, ob.id!);
        reload();
        toast.success(t("step_unlocked"));
      } catch {
        toast.error(t("error"));
      } finally {
        setActionLoading(false);
      }
    });
  };

  const handlePdfView = (fileId: string) => {
    dispatch(setModalxComponent("PdfView"));
    dispatch(setModalxStyle("large"));
    dispatch(setModalxComponentProps({ id: fileId, companyId }));
  };

  const handleCompanyFileUpload = async (files: File[], fileType: string) => {
    if (!companyId || !ob.id) return;
    setUploadingType(fileType);
    for (const file of files) {
      try {
        const { data: fileId } = await upload(file, companyId);
        await uploadOnboardingFile(companyId, ob.id, fileId, fileType);
        reload();
      } catch (error) {
        // An empty file was already toasted by upload() — no second message.
        if (!isEmptyUploadError(error)) toast.error(t("upload_error"));
      }
    }
    setUploadingType(null);
  };

  const handleDeleteOnboardingFile = async (fileId: string) => {
    if (!companyId || !ob.id) return;
    try {
      await deleteOnboardingFile(companyId, ob.id, fileId);
      reload();
    } catch {
      toast.error(t("error"));
    }
  };

  // Step 2 passes the type of the document whose row picked the scan; step 4 has a single document.
  const handleUploadSignedDocument = async (
    e: React.ChangeEvent<HTMLInputElement>,
    documentFileType?: string
  ) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file || !companyId || !ob.id) return;
    setActionLoading(true);
    try {
      const uploadRes = await upload(file, companyId);
      const uploadedFileId =
        uploadRes?.data?.id ||
        uploadRes?.data?.file?.id ||
        uploadRes?.data;
      if (!uploadedFileId) {
        throw new Error("Uploaded file id not found");
      }
      await uploadSignedDocument(
        companyId,
        ob.id,
        documentFileType ? { fileId: uploadedFileId, fileType: documentFileType } : { fileId: uploadedFileId }
      );
      reload();
      toast.success(t("document_uploaded"));
    } catch (error) {
      // An empty file was already toasted by upload() — no second message.
      if (!isEmptyUploadError(error)) {
        toast.error(
          t(
            isFileNotAFreshUpload(error)
              ? "file_not_a_fresh_upload"
              : isDocumentAlreadySigned(error)
                ? "onboarding_document_already_signed"
                : "error"
          )
        );
      }
      // The document may have been signed meanwhile (400 DOCUMENT_ALREADY_SIGNED): show its real state.
      reload();
    } finally {
      setActionLoading(false);
      input.value = "";
    }
  };

  const formatDate = (ts: number | null | undefined) => {
    if (!ts) return "-";
    return new Date(ts).toLocaleDateString("lt-LT");
  };

  // Separate files by type — exclude current step from signed docs (shown in Document block)
  const currentStepFileTypes = isStep2 ? STEP_2_FILE_TYPES : [`${ob.status}_pdf`];
  const stepPdfs = (ob.files || []).filter(
    (f: any) => f.fileType?.includes("_pdf") && !currentStepFileTypes.includes(f.fileType)
  );
  const employeeFiles = (ob.files || []).filter(
    (f: any) => !f.fileType?.includes("_pdf") && f.fileType !== "medical_certificate" && f.fileType !== "step_2_signed_asice"
  );

  const needsPhoneOptions = [
    { value: "", label: "-" },
    { value: "not_needed", label: t("not_needed") },
    { value: "personal", label: t("personal") },
    { value: "new", label: t("new") },
    { value: "any_paid", label: t("any_paid") },
  ];
  const needsComputerOptions = [
    { value: "", label: "-" },
    { value: "not_needed", label: t("not_needed") },
    { value: "existing", label: t("existing") },
    { value: "new", label: t("new") },
  ];
  const companyCarOptions = [
    { value: "", label: "-" },
    { value: "not_needed", label: t("not_needed") },
    { value: "new_owned", label: t("new_owned") },
    { value: "existing_owned", label: t("existing_owned") },
    { value: "rented", label: t("rented") },
    { value: "personal_rent", label: t("personal_rent") },
  ];

  const secondaryActions = ob.status === "cancelled"
    ? [
      { content: t("reactivate"), onAction: handleReactivate, destructive: false },
    ]
    : !isTerminal
      ? [
        { content: t("send_login_link"), onAction: handleSendLoginLink, destructive: false },
        ...(currentStepIndex >= 1 ? ALL_STEPS.slice(0, currentStepIndex).map((step) => ({
          content: `${t("reopen_to")} ${STEP_LABELS[step]?.[lang] || STEP_LABELS[step]?.en || step}`,
          onAction: () => handleReopenStep(step),
          destructive: false,
        })) : []),
        // Early manual activation stays HR-only.
        ...(canManageOnboarding && ob.status !== "work_security" && ob.status !== "step_5" ? [{ content: t("activate_user"), onAction: handleActivateUser, destructive: false }] : []),
        // delete.js: only the creator or a Master cancels.
        ...(isMaster || createdById === currentUserId ? [{ content: t("cancel"), onAction: handleDelete, destructive: true }] : []),
      ]
      : [];

  const isContractRegistration = (ob as any).mode === "contract_registration";

  // «Darbuotojo kortelė»: the person behind this record, once they have an account.
  const employeeUserIdForCard =
    typeof ob.user === "object" ? (ob.user as any)?.id : ob.user;
  const employeeCardLink = employeeUserIdForCard ? (
    <button
      type="button"
      className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:underline max-md:min-h-11"
      onClick={() =>
        navigate(`/app/${companyId}/personnel/${employeeUserIdForCard}`)
      }
    >
      {t("personnel_card_title")}
    </button>
  ) : null;

  const handleUpdateDepartments = () => {
    if (!ob.id || !companyId) return;
    dispatch(setModalxOptions({ title: t("update_departments") }));
    dispatch(setModalxComponent("OnboardingDepartmentModal"));
    dispatch(
      setModalxComponentProps({
        companyId,
        onboardingId: ob.id,
        currentDepartments: ob.departments || [],
      })
    );
    dispatch(setIsModalxOpen(true));
  };

  // Department assignment card + admin-only "Update departments" button.
  // Rendered only for Master in both view modes.
  const departmentsCard = (
    <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 shadow-sm mb-4">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-3">
        <h2 className="text-lg font-semibold">{t("departments")}</h2>
        {!leaveLocked && (
          <button
            className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11"
            onClick={handleUpdateDepartments}
          >
            {t("update_departments")}
          </button>
        )}
      </div>
      {ob.departments && ob.departments.length > 0 ? (
        <div className="flex items-center gap-2 flex-wrap">
          {ob.departments.map((d) => (
            <span
              key={d.id}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
            >
              {d.name}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500">{t("no_departments_assigned")}</p>
      )}
    </div>
  );

  // --- Contract Registration: simplified view ---
  if (isContractRegistration) {
    const contractFile = (ob.files || []).find((f: any) => f.fileType === "step_4_pdf");
    const contractFileObj = contractFile ? (typeof contractFile.file === "object" ? contractFile.file : null) : null;
    const contractFileId = contractFile ? (typeof contractFile.file === "object" ? contractFile.file.id : contractFile.file) : null;

    const translateContractType = (val: string | null | undefined) => {
      if (!val) return "-";
      const key =
        val === "other" ? "contract_type_other_value" : `contract_type_${val}`;
      const translated = t(key);
      return translated !== key ? translated : val;
    };
    const translateContractVariant = (val: string | null | undefined) => {
      if (!val) return "-";
      const key = `contract_variant_${val}`;
      const translated = t(key);
      return translated !== key ? translated : val;
    };

    // Only a second salary that is still ahead is shown (and warned about).
    const plannedSalaryChange = computePlannedSalaryChange(ob.salaryChangeDate, ob.salaryAfterChange);

    const handleEditRegistration = () => {
      dispatch(setModalxComponent("OnboardingForm"));
      dispatch(setModalxComponentProps({ id: ob.id }));
      dispatch(setIsModalxOpen(true));
    };

    return (
      <div className="px-6 py-4">
        <div>
          {/* Header */}
          <div className="mb-2">
            <button
              onClick={() => navigate(`/app/${companyId}/onboarding`)}
              className="text-sm text-blue-600 hover:underline mb-2 flex items-center gap-1 max-md:min-h-11"
            >
              &larr; {t("hr_module")}
            </button>
            <div className="flex items-center justify-between flex-wrap gap-4 mb-3">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <h1 className="text-2xl font-semibold">{`${ob.firstName} ${ob.lastName}`}</h1>
                {ob.positionLT && <span className="text-gray-500">{ob.positionLT}</span>}
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${headerBadgeClasses}`}>
                  {isContractRegistration && !leaveProcessActive
                    ? t("contract_registration")
                    : headerStatusLabel}
                </span>
                {employeeCardLink}
              </div>
              {isMaster && !leaveLocked && (
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11"
                    onClick={handleEditRegistration}
                  >
                    {t("edit")}
                  </button>
                </div>
              )}
            </div>
            {lifecycleTabsBar}
            {stickyStepProgress}
          </div>

          {showLifecycleTabs && lifecycleTab === "leave" && onboardingRecordId ? (
            <OnboardingJobLeaveSection
              companyId={companyId!}
              onboardingId={onboardingRecordId}
              membershipDisabled={membershipDisabled}
              onLeavePresenceChange={setLeaveProcessActive}
              onLeaveStatusChange={setLeaveStickyStatus}
            />
          ) : (
            <>
          {/* Contract Details */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 shadow-sm mb-4">
            <h2 className="text-lg font-semibold mb-3">{t("contract_details")}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-500">{t("original_contract_number")}</p>
                <p className="font-medium">{(ob as any).originalContractNumber || "-"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t("hiring_date")}</p>
                <p>{formatDate(ob.hiringDate)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t("contract_type")}</p>
                <p>{translateContractType(ob.contractType)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t("contract_variant")}</p>
                <p>{translateContractVariant(ob.contractVariant)}</p>
              </div>
              {ob.positionLT && (
                <div>
                  <p className="text-sm text-gray-500">{t("position")}</p>
                  <p>{ob.positionLT}</p>
                </div>
              )}
              <OnboardingSalary
                ob={{
                  salary: ob.currentSalary ?? ob.salary,
                  salaryChangeDate: plannedSalaryChange?.date,
                  salaryAfterChange: plannedSalaryChange?.amount,
                }}
                formatDate={formatDate}
              />
              {ob.hoursPerWeek != null && (
                <div>
                  <p className="text-sm text-gray-500">{t("hours_per_week")}</p>
                  <p>{ob.hoursPerWeek}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500">{t("manager")}</p>
                <p>{managerName || "-"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t("created_by")}</p>
                <p>{createdByName || "-"}</p>
              </div>
            </div>
            {ob.latestTermsAmendment && (
              <p className="mt-3 text-xs text-gray-500">
                {t("contract_terms_updated_by_amendment", {
                  number: ob.latestTermsAmendment.amendmentNumber || "-",
                  date: formatDate(ob.latestTermsAmendment.effectiveDate),
                })}
              </p>
            )}
          </div>

          {isMaster && departmentsCard}

          {/* Contract File */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 shadow-sm mb-4">
            <h2 className="text-lg font-semibold mb-3">{t("contract_file")}</h2>
            {contractFileId ? (
              <div className="flex items-center gap-3">
                <span className="text-sm">
                  {contractFileObj?.title || contractFileObj?.name || contractFileId}
                </span>
                <button
                  className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11"
                  onClick={() => handlePdfView(contractFileId)}
                >
                  {t("view")}
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-500">{t("no_file")}</p>
            )}
          </div>

          {/* Contract Amendments */}
          {canManageOnboarding && ob.id && companyId && (
            <ContractAmendmentsPanel
              companyId={companyId}
              onboardingId={ob.id}
              readOnly={leaveLocked}
              leaveLocked={leaveLocked}
              canManageOnboarding={!leaveLocked}
              lang={lang}
              onSendLoginLink={handleSendLoginLink}
              employeeUserId={
                typeof ob.user === "object" ? (ob.user as any)?.id : ob.user
              }
              employeeName={`${ob.firstName || ""} ${ob.lastName || ""}`.trim()}
              onReloadOnboarding={reload}
              currentTerms={{
                positionLT: ob.positionLT || "",
                positionEN: ob.positionEN || "",
                salary: ob.currentSalary ?? ob.salary ?? "",
                hoursPerWeek: ob.hoursPerWeek ?? "",
                plannedSalaryChange,
              }}
            />
          )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-4">
      <div>
        {/* Back link + header + lifecycle tabs + step status; pinned from md up (on a phone it would cover half the screen) */}
        <div className="md:sticky md:top-0 z-40 -mx-6 px-6 pt-4 pb-3 bg-gray-50 border-b border-gray-200 shadow-sm">
          <button
            onClick={() => navigate(`/app/${companyId}/onboarding`)}
            className="text-sm text-blue-600 hover:underline mb-2 flex items-center gap-1 max-md:min-h-11"
          >
            &larr; {t("hr_module")}
          </button>
          <div className="flex items-center justify-between flex-wrap gap-4 mb-3">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h1 className="text-2xl font-semibold">{`${ob.firstName} ${ob.lastName}`}</h1>
              {ob.positionLT && <span className="text-gray-500">{ob.positionLT}</span>}
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${headerBadgeClasses}`}>
                {headerStatusLabel}
              </span>
              {canManageOnboarding && (
                <AuditLogPopover
                  companyId={companyId!}
                  subjectType="Onboarding"
                  subjectId={onboardingId!}
                  label={t("history", { defaultValue: "History" })}
                />
              )}
              {employeeCardLink}
            </div>
            {leadsThisHire && (
              <div className="flex flex-wrap items-center gap-2">
                {!leaveLocked &&
                  secondaryActions.map((action) => (
                    <button
                      key={action.content}
                      className={
                        action.destructive
                          ? "px-3 py-1.5 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 text-sm font-medium max-md:min-h-11"
                          : "px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11"
                      }
                      onClick={action.onAction}
                    >
                      {action.content}
                    </button>
                  ))}
              </div>
            )}
          </div>
          {lifecycleTabsBar}
          {stickyStepProgress}
        </div>

        <div className="relative z-0 mt-4 isolate">
        {showLifecycleTabs && lifecycleTab === "leave" && onboardingRecordId ? (
          <OnboardingJobLeaveSection
            companyId={companyId!}
            onboardingId={onboardingRecordId}
            membershipDisabled={membershipDisabled}
            onLeavePresenceChange={setLeaveProcessActive}
            onLeaveStatusChange={setLeaveStickyStatus}
          />
        ) : (
          <>
        {isMaster && departmentsCard}

        {/* Kiosk — hire leaders only */}
        {!isTerminal && leadsThisHire && (
          <>
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 shadow-sm">
              <div className="flex flex-col gap-3">
                <h2 className="text-lg font-semibold">{t("kiosk")}</h2>
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex flex-col gap-1 max-w-full break-words">
                    <p className="text-sm text-gray-500">{t("kiosk_code")}</p>
                    <p className="text-xl font-semibold">{kioskCode || "\u2014"}</p>
                  </div>
                  <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11" onClick={handleGetKioskCode}>{t("regenerate_code")}</button>
                </div>
                <p className="text-sm text-gray-500">{t("kiosk_instructions")}</p>
              </div>
            </div>
            <div className="mt-4" />
          </>
        )}

        {/* Step 1 info — hiring questionnaire (managers + medical + work security) */}
        {leadsThisHire && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 shadow-sm">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <h2 className="text-lg font-semibold">{t("hiring_questionnaire")}</h2>
                {!leaveLocked &&
                  ob.status !== "cancelled" &&
                  // patch.js: past step_1 only a Master edits these fields.
                  (ob.status === "step_1" ? leadsThisHire : isMaster) && (
                  <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11" onClick={handleEdit}>{t("edit")}</button>
                )}
              </div>
              <div className="flex items-center gap-x-10 gap-y-3 flex-wrap">
                <div className="flex flex-col gap-1 max-w-full break-words">
                  <p className="text-sm text-gray-500">{t("personal_email")}</p>
                  <p>{ob.personalEmail}</p>
                </div>
                <div className="flex flex-col gap-1 max-w-full break-words">
                  <p className="text-sm text-gray-500">{t("personal_phone")}</p>
                  <p>{ob.personalPhone || "-"}</p>
                </div>
                <div className="flex flex-col gap-1 max-w-full break-words">
                  <p className="text-sm text-gray-500">{t("position_lt")}</p>
                  <p>{ob.positionLT || "-"}</p>
                </div>
                <div className="flex flex-col gap-1 max-w-full break-words">
                  <p className="text-sm text-gray-500">{t("position_en")}</p>
                  <p>{ob.positionEN || "-"}</p>
                </div>
                <div className="flex flex-col gap-1 max-w-full break-words">
                  <p className="text-sm text-gray-500">{t("target_company")}</p>
                  <p>{ob.targetCompany || "-"}</p>
                </div>
                <div className="flex flex-col gap-1 max-w-full break-words">
                  <p className="text-sm text-gray-500">{t("hiring_date")}</p>
                  <p>{formatDate(ob.hiringDate)}</p>
                </div>
                <div className="flex flex-col gap-1 max-w-full break-words">
                  <p className="text-sm text-gray-500">{t("birth_date")}</p>
                  <p>{formatDate(ob.birthDate)}</p>
                </div>
                <OnboardingSalary ob={ob} formatDate={formatDate} className="flex flex-col gap-1 max-w-full break-words" />
                <div className="flex flex-col gap-1 max-w-full break-words">
                  <p className="text-sm text-gray-500">{t("hours_per_week")}</p>
                  <p>{ob.hoursPerWeek || "-"}</p>
                </div>
                <div className="flex flex-col gap-1 max-w-full break-words">
                  <p className="text-sm text-gray-500">{t("work_location")}</p>
                  <p>{ob.workLocation || "-"}</p>
                </div>
                <div className="flex flex-col gap-1 max-w-full break-words">
                  <p className="text-sm text-gray-500">{t("contract_type")}</p>
                  <p>{ob.contractType ? t(`contract_type_${ob.contractType === "other" ? "other_value" : ob.contractType}`) : "-"}</p>
                </div>
                {ob.contractTypeOther && (
                  <div className="flex flex-col gap-1 max-w-full break-words">
                    <p className="text-sm text-gray-500">{t("contract_type_other")}</p>
                    <p>{ob.contractTypeOther}</p>
                  </div>
                )}
                <div className="flex flex-col gap-1 max-w-full break-words">
                  <p className="text-sm text-gray-500">{t("contract_variant")}</p>
                  <p>{ob.contractVariant ? t(`contract_variant_${ob.contractVariant}`) : "-"}</p>
                </div>
                <div className="flex flex-col gap-1 max-w-full break-words">
                  <p className="text-sm text-gray-500">{t("contract_end_date")}</p>
                  <p>{formatDate(ob.contractEndDate)}</p>
                </div>
                <div className="flex flex-col gap-1 max-w-full break-words">
                  <p className="text-sm text-gray-500">{t("probation_months")}</p>
                  <p>{ob.probationMonths != null ? ob.probationMonths : "-"}</p>
                </div>
                <div className="flex flex-col gap-1 max-w-full break-words">
                  <p className="text-sm text-gray-500">{t("manager")}</p>
                  <p>{managerName || "-"}</p>
                </div>
                <div className="flex flex-col gap-1 max-w-full break-words">
                  <p className="text-sm text-gray-500">{t("created_by")}</p>
                  <p>{createdByName || "-"}</p>
                </div>
              </div>
              <OnboardingAssetRequestLine
                companyId={companyId!}
                assetRequest={ob.assetRequest}
                stepOneDone={currentStepIndex > 0 || isTerminal}
              />
            </div>
          </div>
        )}

        <div className="mt-4" />

        {/* Administration Section B — editable; part of the hire (goes into the questionnaire PDF) */}
        {leadsThisHire && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 shadow-sm">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <h2 className="text-lg font-semibold">{t("administration")}</h2>
                {!editingAdmin ? (
                  <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11" onClick={() => setEditingAdmin(true)}>{t("edit")}</button>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    {hasCurrentStepDoc && (
                      <span className="text-sm text-amber-600">{t("edit_will_delete_document")}</span>
                    )}
                    <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11" onClick={() => setEditingAdmin(false)}>{t("cancel")}</button>
                    <button className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium max-md:min-h-11" onClick={handleSaveAdmin}>{t("save")}</button>
                  </div>
                )}
              </div>

              {!editingAdmin ? (
                <div className="flex items-center gap-x-10 gap-y-3 flex-wrap">
                  <div className="flex flex-col gap-1 max-w-full break-words">
                    <p className="text-sm text-gray-500">{t("work_email")}</p>
                    <p>{ob.workEmail || "-"}</p>
                  </div>
                  <div className="flex flex-col gap-1 max-w-full break-words">
                    <p className="text-sm text-gray-500">{t("phone")}</p>
                    <p>{ob.needsNewPhone ? t(ob.needsNewPhone) : "-"}</p>
                  </div>
                  {ob.phoneDevice && (
                    <div className="flex flex-col gap-1 max-w-full break-words">
                      <p className="text-sm text-gray-500">{t("phone_device")}</p>
                      <p>{ob.phoneDevice ? t(ob.phoneDevice) : "-"}</p>
                    </div>
                  )}
                  <div className="flex flex-col gap-1 max-w-full break-words">
                    <p className="text-sm text-gray-500">{t("needs_computer")}</p>
                    <p>{ob.needsComputer ? t(ob.needsComputer) : "-"}</p>
                  </div>
                  <div className="flex flex-col gap-1 max-w-full break-words">
                    <p className="text-sm text-gray-500">{t("computer_details")}</p>
                    <p>{ob.computerDetails || "-"}</p>
                  </div>
                  <div className="flex flex-col gap-1 max-w-full break-words">
                    <p className="text-sm text-gray-500">{t("company_car")}</p>
                    <p>{ob.companyCarType ? t(ob.companyCarType) : "-"}</p>
                  </div>
                  <div className="flex flex-col gap-1 max-w-full break-words">
                    <p className="text-sm text-gray-500">{t("fuel_card")}</p>
                    <p>{ob.needsFuelCard ? t("yes") : ob.needsFuelCard === false ? t("no") : "-"}</p>
                  </div>
                  {ob.hasFuelLimit && (
                    <div className="flex flex-col gap-1 max-w-full break-words">
                      <p className="text-sm text-gray-500">{t("fuel_limit_amount")}</p>
                      <p>{ob.fuelLimit != null ? `${ob.fuelLimit} \u20AC` : "-"}</p>
                    </div>
                  )}
                  <div className="flex flex-col gap-1 max-w-full break-words">
                    <p className="text-sm text-gray-500">{t("needs_server_access")}</p>
                    <p>{ob.needsServerAccess ? t("yes") : ob.needsServerAccess === false ? t("no") : "-"}</p>
                  </div>
                  {ob.needsServerAccess && (
                    <>
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("server_name")}</p>
                        <p>{ob.serverName || "-"}</p>
                      </div>
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("folder_access")}</p>
                        <p>{ob.folderAccess || "-"}</p>
                      </div>
                    </>
                  )}
                  {ob.adminNotes && (
                    <div className="flex flex-col gap-1 max-w-full break-words">
                      <p className="text-sm text-gray-500">{t("notes")}</p>
                      <p>{ob.adminNotes}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("work_email")}</label>
                    <input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" value={adminFields.workEmail} onChange={(e) => setAdminFields({ ...adminFields, workEmail: e.target.value })} />
                  </div>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex-1">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("phone")}</label>
                        <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base md:text-sm" value={adminFields.needsNewPhone} onChange={(e) => setAdminFields({ ...adminFields, needsNewPhone: e.target.value })}>
                          {needsPhoneOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                    </div>
                    {(adminFields.needsNewPhone === "new" || adminFields.needsNewPhone === "personal" || adminFields.needsNewPhone === "any_paid") && (
                      <div className="flex-1">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t("phone_device")}</label>
                          <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base md:text-sm" value={adminFields.phoneDevice} onChange={(e) => setAdminFields({ ...adminFields, phoneDevice: e.target.value })}>
                            {[
                              { value: "", label: "-" },
                              { value: "new", label: t("new") },
                              { value: "existing", label: t("existing") },
                            ].map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </div>
                      </div>
                    )}
                    <div className="flex-1">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("needs_computer")}</label>
                        <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base md:text-sm" value={adminFields.needsComputer} onChange={(e) => setAdminFields({ ...adminFields, needsComputer: e.target.value })}>
                          {needsComputerOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("computer_details")}</label>
                    <input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" value={adminFields.computerDetails} onChange={(e) => setAdminFields({ ...adminFields, computerDetails: e.target.value })} />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-600" checked={adminFields.needsServerAccess} onChange={(e) => setAdminFields({ ...adminFields, needsServerAccess: e.target.checked })} />
                    <span className="text-sm">{t("needs_server_access")}</span>
                  </label>
                  {adminFields.needsServerAccess && (
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex-1">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t("server_name")}</label>
                          <input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" value={adminFields.serverName} onChange={(e) => setAdminFields({ ...adminFields, serverName: e.target.value })} />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t("folder_access")}</label>
                          <input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" value={adminFields.folderAccess} onChange={(e) => setAdminFields({ ...adminFields, folderAccess: e.target.value })} />
                        </div>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("company_car")}</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base md:text-sm" value={adminFields.companyCarType} onChange={(e) => setAdminFields({ ...adminFields, companyCarType: e.target.value })}>
                      {companyCarOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-4 flex-wrap">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-600" checked={adminFields.needsFuelCard} onChange={(e) => setAdminFields({ ...adminFields, needsFuelCard: e.target.checked })} />
                      <span className="text-sm">{t("fuel_card")}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-600" checked={adminFields.hasFuelLimit} onChange={(e) => setAdminFields({ ...adminFields, hasFuelLimit: e.target.checked })} />
                      <span className="text-sm">{t("fuel_limit")}</span>
                    </label>
                  </div>
                  {adminFields.hasFuelLimit && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t("fuel_limit_amount")}</label>
                      <div className="relative">
                        <input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8" value={adminFields.fuelLimit} onChange={(e) => { const v = e.target.value; if (v === "" || /^\d*(\.\d*)?$/.test(v)) setAdminFields({ ...adminFields, fuelLimit: v }); }} />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">&euro;</span>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("notes")}</label>
                    <textarea rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" value={adminFields.adminNotes} onChange={(e) => setAdminFields({ ...adminFields, adminNotes: e.target.value })} />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {leadsThisHire && (
          <>
            <div className="mt-4" />

            {/* Signed PDFs grouped by step — table layout */}
            {(() => {
              const stepGroups: Record<string, any[]> = {};
              for (const f of stepPdfs) {
                const match = f.fileType?.match(/^(step_\d+)/);
                const stepKey = match ? match[1] : "other";
                if (!stepGroups[stepKey]) stepGroups[stepKey] = [];
                stepGroups[stepKey].push(f);
              }
              const groupKeys = Object.keys(stepGroups).sort();
              if (groupKeys.length === 0) return null;
              return (
                <>
                  <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 shadow-sm">
                    <div className="flex flex-col gap-4">
                      <h2 className="text-lg font-semibold">{t("signed_documents")}</h2>
                      <div className="overflow-x-auto">
                        <table className="text-sm w-auto">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-1.5 pr-6 font-medium text-gray-500">{t("status")}</th>
                              <th className="text-left py-1.5 pr-6 font-medium text-gray-500">{t("document")}</th>
                              <th className="text-left py-1.5 font-medium text-gray-500">{t("actions")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupKeys.map((stepKey) => {
                              const files = stepKey === "step_2"
                                ? stepGroups[stepKey].slice().sort(
                                  (a: any, b: any) =>
                                    STEP_2_FILE_TYPES.indexOf(a.fileType) -
                                    STEP_2_FILE_TYPES.indexOf(b.fileType)
                                )
                                : stepGroups[stepKey];
                              const stepLabel = STEP_LABELS[stepKey]?.[lang] || STEP_LABELS[stepKey]?.en || stepKey;
                              const isStep2Group = stepKey === "step_2";
                              return (
                                <React.Fragment key={stepKey}>
                                  <tr>
                                    <td colSpan={3} className="pt-3 pb-1">
                                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{stepLabel}</span>
                                    </td>
                                  </tr>
                                  {files.map((f: any, idx: number) => {
                                    const file = typeof f.file === "object" ? f.file : { id: f.file, name: f.fileType };
                                    const fileId = file.id || f.file;
                                    const label = file.name || file.title || f.fileType;
                                    const isStep1Row = stepKey === "step_1";
                                    const isInformationalRow = f.fileType === STEP_2_QUESTIONNAIRE_FILE_TYPE;
                                    return (
                                      <tr key={f.id} className={idx % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                                        <td className="py-2 pr-6">
                                          {isStep1Row || isInformationalRow ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                              {t("generated")}
                                            </span>
                                          ) : (
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${file.isSigned ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                                              {file.isSigned ? t("signed") : t("pending")}
                                            </span>
                                          )}
                                        </td>
                                        <td className="py-2 pr-6 [overflow-wrap:anywhere]">{label}</td>
                                        <td className="py-2">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <button className="px-2.5 py-1 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-xs font-medium max-md:min-h-11 max-md:px-3 max-md:text-sm" onClick={() => handlePdfView(fileId)}>
                                              {t("view")}
                                            </button>
                                            <button className="px-2.5 py-1 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-xs font-medium max-md:min-h-11 max-md:px-3 max-md:text-sm" onClick={() => window.open(`${API_URL}/${companyId}/files/download/${fileId}`, "_blank")}>
                                              {t("download")}
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4" />
                </>
              );
            })()}
          </>
        )}

        {/* Employee uploaded documents — visible from step 2 onwards */}
        {leadsThisHire && currentStepIndex >= 1 && (
          <>
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 shadow-sm">
              <div className="flex flex-col gap-3">
                <h2 className="text-lg font-semibold">{t("employee_documents")}</h2>
                {employeeFiles.length > 0 ? (
                  employeeFiles.map((f: any) => {
                    const file = typeof f.file === "object" ? f.file : { id: f.file, name: f.fileType };
                    const fileId = file.id || f.file;
                    return (
                      <div key={f.id} className="flex items-center gap-3 flex-wrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{t(f.fileType) || f.fileType}</span>
                        <span>{file.name || fileId}</span>
                        <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11" onClick={() => handlePdfView(fileId)}>
                          {t("view")}
                        </button>
                        <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11" onClick={() => window.open(`${API_URL}/${companyId}/files/download/${fileId}`, "_blank")}>
                          {t("download")}
                        </button>
                        {!isTerminal && (
                          <ConfirmDeleteButton onConfirm={() => handleDeleteOnboardingFile(f.id)} label={t("delete")} confirmLabel={t("confirm_delete")} />
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-500">{t("no_documents_uploaded")}</p>
                )}

                {/* Company-side file upload — available at step 2 */}
                {!isTerminal && ob.status === "step_2" && (
                  <>
                    <p className="text-sm font-semibold text-gray-500">
                      {t("attach_scanned_document")}
                    </p>
                    <div className="flex items-center gap-4 flex-wrap">
                      {[
                        { type: "id_document", label: t("id_document") },
                        { type: "birth_certificate", label: t("birth_certificate") },
                        { type: "drivers_license", label: t("drivers_license") },
                        { type: "education_document", label: t("education_document") },
                        { type: "other", label: t("other_documents") },
                      ].filter((slot) => !employeeFiles.some((f: any) => f.fileType === slot.type))
                        .map((slot) => (
                          <div key={slot.type} className="w-[200px]">
                            <p className="text-sm">{slot.label}</p>
                            {uploadingType === slot.type ? (
                              <div className="flex flex-col items-center justify-center border-2 border-dashed border-blue-300 bg-blue-50 rounded-lg p-4">
                                <div className="flex items-center gap-2">
                                  <span className="animate-spin inline-block h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                                  <span className="text-sm text-blue-600">{t("uploading")}...</span>
                                </div>
                              </div>
                            ) : (
                              <label className={`flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-4 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors ${uploadingType ? "pointer-events-none opacity-50" : ""}`}>
                                <span className="text-sm text-gray-500">PDF, JPG, PNG</span>
                                <input
                                  type="file"
                                  className="hidden"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  disabled={uploadingType !== null}
                                  onChange={(e) => {
                                    const files = e.target.files;
                                    if (files && files.length > 0) {
                                      handleCompanyFileUpload(Array.from(files), slot.type);
                                    }
                                    e.target.value = "";
                                  }}
                                />
                              </label>
                            )}
                          </div>
                        ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="mt-4" />
          </>
        )}

        {/* Employee data (read-only, Steps 2-5 data) */}
        {currentStepIndex >= 1 && (
          <>
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 shadow-sm">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <h2 className="text-lg font-semibold">{t("employee_data")}</h2>
                  {!isTerminal && ob.status === "step_2" && leadsThisHire && (
                    ob.employeeSubmitted ? (
                      <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11" onClick={handleUnlockStep} disabled={actionLoading}>
                        {t("unlock_form")}
                      </button>
                    ) : (
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">{t("form_active")}</span>
                        {companyId && canPrefillEmployeeForm(ob, canManageOnboarding) && (
                          <EmployeeFormPrefill companyId={companyId} onboardingId={ob.id!} onClosed={reload} />
                        )}
                      </div>
                    )
                  )}
                </div>

                {/* Employee personal data — hire leaders only */}
                {leadsThisHire && (
                  <>
                    {/* Personal data */}
                    <div className="flex items-center gap-x-10 gap-y-3 flex-wrap">
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("first_name")}</p>
                        <p>{ob.firstName || "-"}</p>
                      </div>
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("last_name")}</p>
                        <p>{ob.lastName || "-"}</p>
                      </div>
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("personal_code")}</p>
                        <p>{ob.personalCode || "-"}</p>
                      </div>
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("birth_date")}</p>
                        <p>{formatDate(ob.birthDate)}</p>
                      </div>
                    </div>

                    {/* ID Document */}
                    <div className="flex items-center gap-x-10 gap-y-3 flex-wrap">
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("id_document")}</p>
                        <p>{ob.idDocumentType ? t(ob.idDocumentType === "id_card" ? "id_card" : "passport_doc") : "-"} {ob.idDocumentNumber || ""}</p>
                      </div>
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("id_document_issue_date")}</p>
                        <p>{formatDate(ob.idDocumentIssueDate)}</p>
                      </div>
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("id_document_expiry_date")}</p>
                        <p>{formatDate(ob.idDocumentExpiryDate)}</p>
                      </div>
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("id_document_issue_place")}</p>
                        <p>{ob.idDocumentIssuePlace || "-"}</p>
                      </div>
                    </div>

                    {/* Residence permit */}
                    {ob.residencePermitNumber && (
                      <div className="flex items-center gap-x-10 gap-y-3 flex-wrap">
                        <div className="flex flex-col gap-1 max-w-full break-words">
                          <p className="text-sm text-gray-500">{t("residence_permit")}</p>
                          <p>{ob.residencePermitNumber}</p>
                        </div>
                        <div className="flex flex-col gap-1 max-w-full break-words">
                          <p className="text-sm text-gray-500">{t("id_document_issue_date")}</p>
                          <p>{formatDate(ob.residencePermitIssueDate)}</p>
                        </div>
                        <div className="flex flex-col gap-1 max-w-full break-words">
                          <p className="text-sm text-gray-500">{t("id_document_expiry_date")}</p>
                          <p>{formatDate(ob.residencePermitExpiryDate)}</p>
                        </div>
                        <div className="flex flex-col gap-1 max-w-full break-words">
                          <p className="text-sm text-gray-500">{t("id_document_issue_place")}</p>
                          <p>{ob.residencePermitIssuePlace || "-"}</p>
                        </div>
                      </div>
                    )}

                    {/* Education */}
                    <div className="flex items-center gap-x-10 gap-y-3 flex-wrap">
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("education")}</p>
                        <p>{ob.educationLevel ? t(
                          { primary: "primary_edu", basic: "basic_edu", secondary: "secondary_edu", vocational: "vocational", vocational_secondary: "vocational_secondary", higher_non_university: "higher_non_university", higher_university: "higher_university", studying: "studying" }[ob.educationLevel] || ob.educationLevel
                        ) : "-"}</p>
                      </div>
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("education_institution")}</p>
                        <p>{ob.educationInstitution || "-"}</p>
                      </div>
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("education_specialty")}</p>
                        <p>{ob.educationSpecialty || "-"}</p>
                      </div>
                    </div>

                    {/* Address & contacts */}
                    <div className="flex items-center gap-x-10 gap-y-3 flex-wrap">
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("declared_address")}</p>
                        <p>{ob.declaredAddress || "-"}</p>
                      </div>
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("actual_address")}</p>
                        <p>{ob.actualAddress || "-"}</p>
                      </div>
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("personal_phone")}</p>
                        <p>{ob.personalPhone || "-"}</p>
                      </div>
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("personal_email")}</p>
                        <p>{ob.personalEmail || "-"}</p>
                      </div>
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("emergency_contact")}</p>
                        <p>{ob.emergencyContact || "-"}</p>
                      </div>
                    </div>

                    {/* Family */}
                    <div className="flex items-center gap-x-10 gap-y-3 flex-wrap">
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("children_under_12")}</p>
                        <p>{ob.hasChildrenUnder12 ? t("yes") : t("no")}</p>
                      </div>
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("children_under_14")}</p>
                        <p>{ob.hasChildrenUnder14 ? t("yes") : t("no")}</p>
                      </div>
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("disabled_children_under_18")}</p>
                        <p>{ob.hasDisabledChildrenUnder18 ? t("yes") : t("no")}</p>
                      </div>
                    </div>

                    {/* Children list */}
                    {ob.children && ob.children.length > 0 && (
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("children_info")}</p>
                        {ob.children.map((child: any, idx: number) => (
                          <p key={child.id || idx}>
                            {child.firstName} {child.lastName}{child.personalCode ? ` (${child.personalCode})` : ""}
                          </p>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Disability — GDPR health data (managers + medical + work security) */}
                {leadsThisHire && (
                  <div className="flex items-center gap-x-10 gap-y-3 flex-wrap">
                    <div className="flex flex-col gap-1 max-w-full break-words">
                      <p className="text-sm text-gray-500">{t("has_disability")}</p>
                      <p>{ob.hasDisability ? t("yes") : t("no")}</p>
                    </div>
                    {ob.hasDisability && (
                      <>
                        <div className="flex flex-col gap-1 max-w-full break-words">
                          <p className="text-sm text-gray-500">{t("disability_cert_number")}</p>
                          <p>{ob.disabilityCertNumber || "-"}</p>
                        </div>
                        <div className="flex flex-col gap-1 max-w-full break-words">
                          <p className="text-sm text-gray-500">{t("disability_cert_expiry")}</p>
                          <p>{formatDate(ob.disabilityCertExpiry)}</p>
                        </div>
                        <div className="flex flex-col gap-1 max-w-full break-words">
                          <p className="text-sm text-gray-500">{t("work_capacity_percent")}</p>
                          <p>{ob.workCapacityPercent != null ? `${ob.workCapacityPercent}%` : "-"}</p>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Conflict of interest, additional info, consent, bank — hire leaders only */}
                {leadsThisHire && (
                  <>
                    {/* Conflict of interest */}
                    <div className="flex items-center gap-x-10 gap-y-3 flex-wrap">
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("has_conflict_of_interest")}</p>
                        <p>{ob.hasConflictOfInterest ? t("yes") : t("no")}</p>
                      </div>
                      {ob.hasConflictOfInterest && ob.conflictOfInterestDetails && (
                        <div className="flex flex-col gap-1 max-w-full break-words">
                          <p className="text-sm text-gray-500">{t("conflict_of_interest_details")}</p>
                          <p>{ob.conflictOfInterestDetails}</p>
                        </div>
                      )}
                    </div>

                    {/* Additional info */}
                    <div className="flex flex-col gap-1 max-w-full break-words">
                      <p className="text-sm text-gray-500">{t("additional_info_field")}</p>
                      <p>{ob.additionalInfo || "-"}</p>
                    </div>

                    {/* Data processing consent */}
                    <div className="flex flex-col gap-1 max-w-full break-words">
                      <p className="text-sm text-gray-500">{t("data_processing_consent")}</p>
                      <p>{ob.dataProcessingConsent ? t(ob.dataProcessingConsent) : "-"}</p>
                    </div>

                    {/* Step 4 data: Bank & payments */}
                    <div className="flex items-center gap-x-10 gap-y-3 flex-wrap">
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("bank_account")}</p>
                        <p>{ob.bankAccount || "-"}</p>
                      </div>
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("payment_frequency")}</p>
                        <p>{ob.paymentFrequency ? t(ob.paymentFrequency) : "-"}</p>
                      </div>
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("npd_option")}</p>
                        <p>{ob.npdOption ? t(ob.npdOption) : "-"}</p>
                      </div>
                      <div className="flex flex-col gap-1 max-w-full break-words">
                        <p className="text-sm text-gray-500">{t("disability_level")}</p>
                        <p>{ob.disabilityLevel || "-"}</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="mt-4" />
          </>
        )}

        {/* GDPR consents (read-only) — hire leaders only */}
        {leadsThisHire && ob.gdprConsent1BirthdayGreeting != null && (
          <>
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 shadow-sm">
              <div className="flex flex-col gap-3">
                <h2 className="text-lg font-semibold">{t("gdpr_consents")}</h2>
                <div className="flex items-center gap-6 flex-wrap">
                  {[
                    { key: "gdprConsent1BirthdayGreeting", label: t("birthday_greeting") },
                    { key: "gdprConsent2PhotoSessions", label: t("photo_sessions") },
                    { key: "gdprConsent3Website", label: t("website_team") },
                    { key: "gdprConsent4SocialMedia", label: t("social_media") },
                    { key: "gdprConsent5ClientPresentations", label: t("client_presentations") },
                    { key: "gdprConsent6InternalPhotoVideo", label: t("internal_photo_video") },
                    { key: "gdprConsent7InternalNetwork", label: t("internal_network") },
                  ].map((c) => (
                    <div key={c.key} className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${(ob as any)[c.key] ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                        {(ob as any)[c.key] ? t("yes") : t("no")}
                      </span>
                      <span className="text-sm">{c.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-4" />
          </>
        )}

        {/* === Medical data — status + validity (after GDPR consents) === */}
        {leadsThisHire && ob.medicalCheckApproved && (
          <>
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 shadow-sm">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <h2 className="text-lg font-semibold">{t("medical_data")}</h2>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">{t("medical_check_approved")}</span>
                </div>
                {ob.medicalCheckValidUntil && (
                  <p className="text-sm text-gray-500">
                    {t("medical_validity")}: {formatDate(ob.medicalCheckValidFrom)} – {formatDate(ob.medicalCheckValidUntil)}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-4" />
          </>
        )}

        {/* === BLOCK 1: Document (steps 1, 2, 4) === */}
        {leadsThisHire && !isTerminal && !isStep3 && !isWorkSecurity && !isStep5 && (
          <>
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 shadow-sm">
              <div className="flex flex-col gap-3">
                <h2 className="text-lg font-semibold">{isStep2 ? t("documents") : t("document")}</h2>
                {hasCurrentStepDoc ? (
                  <>
                    {/* Show generated PDFs with per-file signed/pending badge */}
                    {currentStepFiles.map((sf: any) => {
                      const fileObj = typeof sf.file === "object" ? sf.file : null;
                      if (!fileObj) return null;
                      // The employee questionnaire (step_2) is informational — not signed.
                      const isInformational = sf.fileType === STEP_2_QUESTIONNAIRE_FILE_TYPE;
                      // step_1 has no signing — show a neutral "ready" badge instead.
                      const renderBadge = () => {
                        if (isStep1 || isInformational) {
                          return (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {t("document_ready")}
                            </span>
                          );
                        }
                        if (fileObj.isSigned) {
                          return (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {t("signed")}
                            </span>
                          );
                        }
                        return (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            {t("pending")}
                          </span>
                        );
                      };
                      return (
                        <div key={sf.id} className="flex items-center gap-3 flex-wrap">
                          {renderBadge()}
                          <span className="text-sm">{fileObj.name || fileObj.title || sf.fileType}</span>
                          <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11" onClick={() => handlePdfView(fileObj.id)}>
                            {t("view_pdf")}
                          </button>
                          <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11" onClick={() => window.open(`${API_URL}/${companyId}/files/download/${fileObj.id}`, "_blank")}>
                            {t("download")}
                          </button>
                          {isStep2 && showManualUpload && !isInformational && !fileObj.isSigned && (
                            <label className="inline-block px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium cursor-pointer has-[:disabled]:opacity-50 has-[:disabled]:cursor-default max-md:min-h-11 max-md:inline-flex max-md:items-center">
                              {t("upload_signed_document")}
                              <input
                                type="file"
                                accept=".pdf"
                                className="hidden"
                                aria-label={`${t("upload_signed_document")}: ${fileObj.name || fileObj.title || sf.fileType}`}
                                disabled={actionLoading}
                                onChange={(e) => handleUploadSignedDocument(e, sf.fileType)}
                              />
                            </label>
                          )}
                        </div>
                      );
                    })}
                    {allSigned && (
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">{t("all_signed")}</span>
                      </div>
                    )}
                    {/* Regenerate — always available */}
                    <div className="flex items-end gap-4 flex-wrap mt-2">
                      <button
                        className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2 max-md:min-h-11"
                        onClick={() =>
                          askConfirm(
                            t(isStep1 ? "confirm_regenerate_document_no_sign" : "confirm_regenerate_document"),
                            handleGenerateDocument,
                            true
                          )
                        }
                        disabled={actionLoading}
                      >
                        {t("regenerate_documents")}
                      </button>
                      {tabletDocumentCancelled && (
                        <span className="text-sm text-amber-600">{t("onboarding_signontab_document_cancelled")}</span>
                      )}
                      {/* Step 1: no signing — just advance to step 2 */}
                      {isStep1 && (
                        <button
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2 max-md:min-h-11"
                          onClick={handleCompleteStep1}
                          disabled={actionLoading}
                        >
                          {actionLoading && <span className="animate-spin inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full" />}
                          {t("complete_step_1")}
                        </button>
                      )}
                      {/* Steps 2 & 4: Send for signature — only when not signed and no pending signatures */}
                      {!isStep1 && !allSigned && !hasPendingSignatures && (
                        <>
                          <SignMethodSelect key={companyId} value={signMethodValue} onChange={setSignMethodValue} companyId={companyId!} />
                          <button
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2 max-md:min-h-11"
                            onClick={handleSendForSignature}
                            disabled={actionLoading || (signMethodValue.method === "signontab" && !signMethodValue.selectedTabUser)}
                          >
                            {actionLoading && <span className="animate-spin inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full" />}
                            {t("send_for_signature")}
                          </button>
                        </>
                      )}
                    </div>
                    {/* Manual upload */}
                    {/* Only a PDF is accepted, and a phone camera photo is not one:
                        say how to get a PDF from the phone. */}
                    {showManualUpload && !allSigned && (
                      <p className="text-xs text-gray-500 md:hidden">{t("scan_to_pdf_hint")}</p>
                    )}
                    {/* Step 2 has an upload on each document row above. */}
                    {showManualUpload && !isStep2 && (
                      <div className="mt-2">
                        <label className="inline-block px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium cursor-pointer has-[:disabled]:opacity-50 has-[:disabled]:cursor-default max-md:min-h-11 max-md:inline-flex max-md:items-center">
                          {t("upload_signed_document")}
                          <input
                            type="file"
                            accept=".pdf"
                            className="hidden"
                            disabled={actionLoading}
                            onChange={handleUploadSignedDocument}
                          />
                        </label>
                      </div>
                    )}
                  </>
                ) : !isEmployeeDataReady ? (
                  <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 rounded-lg flex items-center justify-between gap-4 flex-wrap">
                    <span>{t("employee_data_required")}</span>
                    <button
                      className="px-3 py-1.5 bg-white border border-yellow-300 text-yellow-900 rounded-lg hover:bg-yellow-100 text-sm font-medium disabled:opacity-50 whitespace-nowrap max-md:min-h-11"
                      onClick={handleSendLoginLink}
                      disabled={actionLoading}
                    >
                      {t("send_login_link")}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-end gap-4 flex-wrap">
                    <button
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2 max-md:min-h-11"
                      onClick={handleGenerateDocument}
                      disabled={actionLoading}
                    >
                      {actionLoading && <span className="animate-spin inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full" />}
                      {actionLoading ? t("generating") + "..." : t("generate_documents")}
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4" />
          </>
        )}

        {/* === Medical Check (step 3) — whoever runs the hire.
             Upload + approve (and revoke). Hidden once approved (block leaves step 3). === */}
        {leadsThisHire && !isTerminal && isStep3 && (
          <>
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 shadow-sm">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <h2 className="text-lg font-semibold">{t("medical_check")}</h2>
                  {ob.medicalCheckApproved ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">{t("medical_check_approved")}</span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">{t("medical_check_pending")}</span>
                  )}
                </div>
                {ob.medicalCheckApproved && (ob as any).canApproveMedical && (
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      className="px-3 py-1.5 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 text-sm font-medium disabled:opacity-50 max-md:min-h-11"
                      onClick={handleRevokeMedical}
                      disabled={actionLoading}
                    >
                      {t("revoke_medical_approval")}
                    </button>
                  </div>
                )}
                {!ob.medicalCheckApproved && (
                  <>
                {/* Medical files list */}
                {medicalFiles.map((mf: any) => {
                  const fileObj = typeof mf.file === "object" ? mf.file : null;
                  return (
                    <div key={mf.id} className="flex items-center gap-3 flex-wrap">
                      <span className="text-sm">{fileObj?.name || fileObj?.title || t("medical_certificate")}</span>
                      {fileObj && (
                        <>
                          <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11" onClick={() => handlePdfView(fileObj.id)}>
                            {t("view")}
                          </button>
                          <ConfirmDeleteButton onConfirm={() => handleDeleteOnboardingFile(mf.id)} label={t("delete")} confirmLabel={t("confirm_delete")} />
                        </>
                      )}
                    </div>
                  );
                })}
                {/* Upload medical file */}
                <div>
                  <label className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium cursor-pointer inline-block max-md:min-h-11 max-md:inline-flex max-md:items-center">
                    {t("upload_medical_files")}
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files) handleCompanyFileUpload(Array.from(e.target.files), "medical_certificate");
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
                {/* Approve */}
                <div className="flex items-center gap-3 mt-2">
                  {hasMedicalFiles && (ob as any).canApproveMedical && (
                    <button
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50 max-md:min-h-11"
                      onClick={openApproveMedical}
                      disabled={actionLoading}
                    >
                      {t("approve_medical")}
                    </button>
                  )}
                  {hasMedicalFiles && !(ob as any).canApproveMedical && (
                    <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg text-sm">
                      <p className="font-medium">{t("medical_approve_not_authorized")}</p>
                      {(ob as any).medicalApprovers?.length > 0 && (
                        <p className="mt-1">{t("can_approve")}: {(ob as any).medicalApprovers.join(", ")}</p>
                      )}
                    </div>
                  )}
                </div>
                  </>
                )}
              </div>
            </div>
            <div className="mt-4" />
          </>
        )}

        {/* === BLOCK 2: Signing Status — hire leaders only === */}
        {leadsThisHire && !isTerminal && !isStep1 && !isStep3 && !isWorkSecurity && !isStep5 && hasCurrentStepDoc && (
          <>
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 shadow-sm">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <h2 className="text-lg font-semibold">{t("signing_status")}</h2>
                  {allSigned ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">{t("all_signed")}</span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">{t("waiting_for_signatures")}</span>
                  )}
                </div>

                {currentStepSignaturesByUser.map((sig: any) => {
                  const resendSignatureId = sig.resendSignatureId || sig.id;
                  return (
                    <div key={sig.id} className="flex items-center gap-3 flex-wrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${sig.status === "signed" ? "bg-green-100 text-green-800" : sig.status === "declined" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"}`}>
                        {t(sig.status)}
                      </span>
                      <span>{getSignatureUserLabel(sig)}</span>
                      {sig.signingTime && (
                        <span className="text-sm text-gray-500">
                          {new Date(sig.signingTime).toLocaleString("lt-LT")}
                        </span>
                      )}
                      {sig.status === "pending" && resendSignatureId && !stepSignsOnTablet && (
                        resentIds.has(resendSignatureId) ? (
                          <span className="text-xs text-green-600 font-medium">{t("reminder_sent")}</span>
                        ) : (
                          <button
                            className="px-2 py-1 text-xs bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50 flex items-center gap-1 max-md:min-h-11 max-md:px-3 max-md:text-sm"
                            onClick={() => handleResendSignature(resendSignatureId)}
                            disabled={resendingId === resendSignatureId}
                          >
                            {resendingId === resendSignatureId && (
                              <span className="animate-spin inline-block h-3 w-3 border-2 border-gray-400 border-t-transparent rounded-full" />
                            )}
                            {t("resend")}
                          </button>
                        )
                      )}
                    </div>
                  );
                })}

                {currentStepSignaturesByUser.length === 0 && (
                  <p className="text-gray-500">{t("waiting_for_signatures")}</p>
                )}

                {!allSigned && (
                  <div className="flex items-center gap-3 flex-wrap">
                    {stepSignsOnTablet && (
                      <span className="text-sm text-gray-500">{t("amendment_signontab_waiting")}</span>
                    )}
                    {hasPendingSignatures && currentUserPendingStepFileId && !stepSignsOnTablet && (
                      <button
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 max-md:min-h-11"
                        onClick={() => handleSign(currentUserPendingStepFileId)}
                      >
                        {t("sign_document")}
                      </button>
                    )}
                    <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11" onClick={() => reload()} disabled={actionLoading}>
                      {t("refresh")}
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4" />
          </>
        )}


        {/* === Work Security (between contract and activation) === */}
        {leadsThisHire && isWorkSecurity && (
          <>
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 shadow-sm">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-semibold">
                      {STEP_LABELS.work_security?.[lang] || STEP_LABELS.work_security?.en}
                    </h2>
                    {wsStatus && wsTotalDocuments > 0 && (
                      <Badge tone={wsStatus.unsignedCount === 0 ? "success" : "attention"}>
                        {t("ws_signed_count", { signed: wsSignedDocuments, total: wsTotalDocuments })}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* No manual role picking here any more: pareigybės come
                        from the positions, approved per sphere; individual
                        ones are added in the approval panel / employee card. */}
                    <button
                      className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium disabled:opacity-50 max-md:min-h-11"
                      onClick={refreshWsStep}
                      disabled={wsLoading}
                    >
                      {t("refresh")}
                    </button>
                  </div>
                </div>

                {!wsStatus ? (
                  wsLoadFailed ? (
                    <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center justify-between gap-4 flex-wrap">
                      <span className="text-sm">{t("ws_load_failed")}</span>
                      <button
                        className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11"
                        onClick={() => loadWsStatus()}
                      >
                        {t("refresh")}
                      </button>
                    </div>
                  ) : (
                    <div className="flex justify-center p-6">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                    </div>
                  )
                ) : (
                  <>
                    {/* The employee signs in My Onboarding — remind about the login link */}
                    {leadsThisHire && (
                      <div className="bg-blue-50 border border-blue-200 text-blue-800 p-4 rounded-lg flex items-center justify-between gap-4 flex-wrap">
                        <span className="text-sm">{t("ws_send_login_link_hint")}</span>
                        <button
                          className="px-3 py-1.5 bg-white border border-blue-300 text-blue-900 rounded-lg hover:bg-blue-100 text-sm font-medium disabled:opacity-50 whitespace-nowrap max-md:min-h-11"
                          onClick={handleSendLoginLink}
                          disabled={actionLoading}
                        >
                          {t("send_login_link")}
                        </button>
                      </div>
                    )}

                    <ApprovalStatusLine rows={hireApprovals} />

                    {(wsStatus.roles || []).length === 0 ? (
                      <p className="text-gray-500">{t("no_roles_assigned")}</p>
                    ) : wsHasOptionalRoles ? (
                      <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            {t("ws_required_now")}
                          </p>
                          {wsRequiredRoles.map(renderWsRoleCard)}
                        </div>
                        {wsOptionalRoles.length > 0 && (
                          <div className="flex flex-col gap-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                              {t("ws_other_instructions")}
                            </p>
                            {wsOptionalRoles.map(renderWsRoleCard)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col gap-4">
                        {wsStatus.roles.map(renderWsRoleCard)}
                      </div>
                    )}

                    {/* Roles without an active document — nothing will be
                        signed, so they never hold the step (information only) */}
                    {(wsStatus.rolesWithoutActiveDocuments || []).length > 0 && (
                      <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 rounded-lg">
                        <p className="text-sm font-medium">
                          {t("ws_docless_roles_warning")}{" "}
                          {wsStatus.rolesWithoutActiveDocuments.map((r) => r.name).join(", ")}
                        </p>
                      </div>
                    )}

                    {/* The step skips itself once no blocking sphere is pending
                        and no required document is unsigned; the manual button
                        is HR's (Master / 'Access onboarding'); a hiring head
                        reads the hint. */}
                    {!shouldOfferManualWsComplete(wsStatus, hireApprovals) || !canManageOnboarding ? (
                      <div className="pt-2">
                        <p className="text-sm text-gray-600">
                          {isWaitingForBlockingApprovals(wsStatus, hireApprovals)
                            ? t("ws_step_waiting_approvals")
                            : t("ws_step_auto_complete_hint")}
                        </p>
                        {(wsStatus.requiredUnsignedCount || 0) > 0 && (
                          <p className="text-sm text-red-500 mt-1">{t("ws_error_unsigned")}</p>
                        )}
                      </div>
                    ) : (
                    <div className="pt-2">
                      <button
                        className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                        disabled={
                          actionLoading ||
                          wsLoading ||
                          // Only roles required at hiring gate the step — an
                          // optional instruction area's unsigned document
                          // must never block completion (see complete-work-security.js).
                          (wsStatus.requiredUnsignedCount || 0) > 0
                        }
                        onClick={handleCompleteWorkSecurity}
                      >
                        {actionLoading && (
                          <span className="animate-spin inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        )}
                        {t("complete_step")}
                      </button>
                    </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="mt-4" />
          </>
        )}

        {/* Work-security status unavailable at activation — the gate is
             fail-closed client-side (activation submit stays disabled), so
             give the admin a retry surface. */}
        {leadsThisHire && isStep5 && !wsStatus && wsLoadFailed && (
          <>
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center justify-between gap-4 flex-wrap">
              <span className="text-sm">{t("ws_load_failed")}</span>
              <button
                className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11"
                onClick={() => loadWsStatus()}
              >
                {t("refresh")}
              </button>
            </div>
            <div className="mt-4" />
          </>
        )}

        {/* Work-security gate warning at activation — unsigned docs appeared after
             the step was completed; the backend blocks activation regardless.
             Only required-at-hiring roles actually block activation (see
             complete-activation.js), so the list below is filtered to those —
             an optional instruction left unsigned must not show up as a
             blocker here. */}
        {leadsThisHire && isStep5 && wsStatus && wsStatus.requiredUnsignedCount > 0 && (
          <>
            <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg flex flex-col gap-2">
              <p className="font-medium">{t("ws_unsigned_docs_warning")}</p>
              <ul className="list-disc list-inside text-sm">
                {(wsStatus.roles || [])
                  .filter((item) => item.requiredAtHiring !== false)
                  .flatMap((item) =>
                    (item.documents || [])
                      .filter((doc) => doc.signature?.status !== "signed")
                      .map((doc) => (
                        <li key={doc.id}>
                          {item.role.name} — {doc.fileName}
                        </li>
                      ))
                  )}
              </ul>
              {leadsThisHire && (
                <div>
                  <button
                    className="px-3 py-1.5 bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-100 text-sm font-medium max-md:min-h-11"
                    onClick={() => handleReopenStep("work_security")}
                  >
                    {t("ws_reopen_step")}
                  </button>
                </div>
              )}
            </div>
            <div className="mt-4" />
          </>
        )}

        {/* === Step 5: Employee Activation === */}
        {leadsThisHire && isStep5 && (
          <>
            <div className="relative z-0 bg-white rounded-lg border border-gray-200 p-4 sm:p-5 shadow-sm">
              <div className="flex flex-col gap-4">
                <h2 className="text-lg font-semibold">{t("employee_activation_setup")}</h2>

                {!activationDataLoaded ? (
                  <div className="flex justify-center p-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                  </div>
                ) : (
                  <>
                    {!canAssignRights && (
                      <p className="text-sm text-gray-600">{t("activation_personal_area_only")}</p>
                    )}
                    {canAssignRights && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-700">
                          {t("activation_permissions_title")}
                        </h3>
                        <p className="mb-3 mt-0.5 text-xs text-gray-500">
                          {t("activation_permissions_hint")}
                        </p>
                        <PermissionPicker
                          catalogue={permissionCatalogue}
                          value={activationPermissions}
                          positionPermissions={positionPermissions}
                          canAssignMaster={canAssignMaster}
                          onChange={setActivationPermissions}
                        />
                      </div>
                    )}

                    <div>
                      <UserSelect
                        label="manager"
                        value={activationManager}
                        onChange={(v: string) => setActivationManager(v)}
                        onlyActivated
                      />
                    </div>

                    <div>
                      <DepartmentMultiSelect
                        companyId={companyId || ""}
                        value={activationDepartments}
                        onChange={setActivationDepartments}
                        options={assignableDepartments}
                      />
                    </div>

                    {canAssignRights && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t("select_folder_access")}
                        </label>
                        {categoryTree.length > 0 ? (
                          <div className="border border-gray-200 rounded-lg p-3 md:max-h-64 md:overflow-y-auto">
                            {categoryTree.map((node: any) => (
                              <CategoryNode
                                key={node.id}
                                node={node}
                                selected={activationCategories}
                                onToggle={(id: string) => {
                                  setActivationCategories((prev) =>
                                    prev.includes(id)
                                      ? prev.filter((c) => c !== id)
                                      : [...prev, id]
                                  );
                                }}
                                depth={0}
                              />
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">{t("no_categories")}</p>
                        )}
                      </div>
                    )}

                    <div className="pt-2">
                      <button
                        className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                        disabled={
                          actionLoading ||
                          // Fail-closed: block while the work-security status
                          // is unknown (loading/failed), not only when unsigned.
                          // Only roles required at hiring gate activation — an
                          // optional instruction area must never block it
                          // (see complete-activation.js).
                          !wsStatus ||
                          (wsStatus.requiredUnsignedCount || 0) > 0 ||
                          // The backend also refuses while a blocking sphere
                          // approval is pending (WORK_SECURITY_APPROVALS_PENDING).
                          (wsStatus.pendingBlockingApprovals || 0) > 0
                        }
                        onClick={async () => {
                          if (!companyId || !ob.id) return;
                          setActionLoading(true);
                          try {
                            await completeActivation(companyId, ob.id, {
                              ...(canAssignRights
                                ? { permissions: activationPermissions, categories: activationCategories }
                                : {}),
                              manager: activationManager || null,
                              departments: activationDepartments,
                            });
                            toast.success(t("employee_activated"));
                            reload();
                          } catch (e: any) {
                            const code = e?.response?.data?.error ?? e?.response?.data?.code;
                            if (code === "WORK_SECURITY_UNSIGNED") {
                              toast.error(t("ws_error_unsigned"));
                              loadWsStatus();
                            } else if (code === "WORK_SECURITY_APPROVALS_PENDING") {
                              // Activation is gated on pending sphere approvals too.
                              toast.error(t("ws_error_approvals_pending"));
                              loadWsStatus();
                            } else if (code === "WORK_SECURITY_ROLES_NOT_APPLIED") {
                              toast.error(t("ws_error_roles_not_applied"));
                              loadWsStatus();
                            } else if (code === "MASTER_ASSIGN_FORBIDDEN") {
                              toast.error(t("activation_master_assign_forbidden"));
                            } else if (code === "GROUPS_RETIRED") {
                              // This page is older than the server: it still sends groups.
                              toast.error(t("groups_retired_reload"));
                            } else if (code === "CONFLICT" || e?.response?.status === 409) {
                              toast.error(t("ws_error_conflict"));
                              reload();
                            } else {
                              const departmentKey = onboardingDepartmentErrorKey(e);
                              toast.error(departmentKey ? t(departmentKey) : code || t("error"));
                            }
                          } finally {
                            setActionLoading(false);
                          }
                        }}
                      >
                        {actionLoading && (
                          <span className="animate-spin inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        )}
                        {t("activate_employee")}
                      </button>
                      {(wsStatus?.pendingBlockingApprovals || 0) > 0 && (
                        <p className="text-sm text-red-500 mt-1">{t("ws_error_approvals_pending")}</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="mt-4" />
          </>
        )}

        {/* Contract Amendments — visible once hire is past contract / completed */}
        {canManageOnboarding && (isWorkSecurity || isStep5 || isTerminal) && ob.id && companyId && (
          <ContractAmendmentsPanel
            companyId={companyId}
            onboardingId={ob.id}
            readOnly={leaveLocked}
            leaveLocked={leaveLocked}
            canManageOnboarding={!leaveLocked}
            lang={lang}
            onSendLoginLink={handleSendLoginLink}
            employeeUserId={
              typeof ob.user === "object" ? (ob.user as any)?.id : ob.user
            }
            employeeName={`${ob.firstName || ""} ${ob.lastName || ""}`.trim()}
            onReloadOnboarding={reload}
            currentTerms={{
              positionLT: ob.positionLT || "",
              positionEN: ob.positionEN || "",
              salary: ob.currentSalary ?? ob.salary ?? "",
              hoursPerWeek: ob.hoursPerWeek ?? "",
              plannedSalaryChange: computePlannedSalaryChange(ob.salaryChangeDate, ob.salaryAfterChange),
            }}
          />
        )}
          </>
        )}
        </div>

      </div>
      <ConfirmDialog
        open={!!confirmState}
        message={confirmState?.message || ""}
        destructive={confirmState?.destructive}
        onConfirm={handleConfirmYes}
        onClose={closeConfirm}
      />
    </div>
  );
}

/** Inline confirm delete button — no native confirm dialog */
function ConfirmDeleteButton({ onConfirm, label, confirmLabel }: { onConfirm: () => void; label: string; confirmLabel: string }) {
  const [confirming, setConfirming] = React.useState(false);

  if (confirming) {
    return (
      <div className="flex items-center gap-1 max-md:gap-2">
        <span className="text-xs text-red-600 font-medium">{confirmLabel}</span>
        <button className="px-2 py-1 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 max-md:min-h-11 max-md:px-3 max-md:text-sm" onClick={() => { setConfirming(false); onConfirm(); }}>
          {label === "delete" ? "✓" : "✓"}
        </button>
        <button className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs font-medium hover:bg-gray-300 max-md:min-h-11 max-md:px-3 max-md:text-sm" onClick={() => setConfirming(false)}>
          ✕
        </button>
      </div>
    );
  }

  return (
    <button className="px-3 py-1.5 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 text-sm font-medium max-md:min-h-11" onClick={() => setConfirming(true)}>
      {label}
    </button>
  );
}

/** Recursive category checkbox node; each level indents 12px on a phone, 20px from sm up. */
function CategoryNode({ node, selected, onToggle, depth }: { node: any; selected: string[]; onToggle: (id: string) => void; depth: number }) {
  return (
    <div>
      <label
        className="flex items-center gap-2 py-2 md:py-1 cursor-pointer hover:bg-gray-50 rounded px-1 pl-[calc(var(--depth)*12px)] sm:pl-[calc(var(--depth)*20px)]"
        style={{ "--depth": depth } as React.CSSProperties}
      >
        <input
          type="checkbox"
          checked={selected.includes(node.id)}
          onChange={() => onToggle(node.id)}
          className="rounded border-gray-300 max-md:h-5 max-md:w-5"
        />
        <span className="text-sm">{node.title || node.name}</span>
      </label>
      {node.subcategories?.map((child: any) => (
        <CategoryNode key={child.id} node={child} selected={selected} onToggle={onToggle} depth={depth + 1} />
      ))}
    </div>
  );
}

export default OnboardingViewContainer;
